/* RecordDrawer (right slide-over), Copilot, Command palette
   All wired to centralized useStore() — real inline edit, delete, dirty state, ESC, notes & tasks. */

const { useState: useStateC, useEffect: useEffectC, useRef: useRefC, useMemo: useMemoC } = React;

/* =========================================================
   RECORD DRAWER — right-side slide-over (fully wired)
   ========================================================= */
const DRAWER_FOCUSABLE = 'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

const RecordDrawer = () => {
  const store = useStore();
  const _stages = store.pipeline || [];
  const record = store.drawer;
  const [tab, setTab] = useStateC('overview');
  const [expanded, setExpanded] = useStateC(false);
  const [activityIntent, setActivityIntent] = useStateC(null);
  const [emailAutoCompose, setEmailAutoCompose] = useStateC(false);
  const drawerRef   = useRefC(null);
  const prevFocusRef = useRefC(null);
  const [statusPopoverOpen, setStatusPopoverOpen] = useStateC(false);
  const statusPopoverRef = useRefC(null);
  const [descEditing, setDescEditing] = useStateC(false);
  const [descDraft, setDescDraft] = useStateC('');
  const [descSaving, setDescSaving] = useStateC(false);
  const descTextareaRef = useRefC(null);
  const tabsRef = useRefC(null);
  const [tabScroll, setTabScroll] = useStateC({ canLeft: false, canRight: false });

  /* Escape key — close drawer. Dep narrowed to closeDrawer (stable ref) to avoid
     re-subscribing on every store state change. */
  useEffectC(() => {
    if (!record) return;
    const onKey = (e) => { if (e.key === 'Escape') store.closeDrawer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [record, store.closeDrawer]);

  /* Reset tab and description editor when switching records */
  useEffectC(() => {
    if (record) {
      const isTaskRecord = 'done' in record;
      setTab(window.__nx_discuss_intent ? 'discuss' : isTaskRecord ? 'home' : 'overview');
      setDescEditing(false);
      setDescDraft('');
      setDescSaving(false);
    }
  }, [record?.id]);

  /* Clear back-stack when drawer closes */
  useEffectC(() => { if (!record) window.__nx_drawer_back = null; }, [record]);

  /* Body scroll lock — prevents page from scrolling behind the open drawer */
  useEffectC(() => {
    if (!record) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [record]);

  /* Focus management — save caller focus, move focus into drawer, restore on close */
  useEffectC(() => {
    if (record) {
      prevFocusRef.current = document.activeElement;
      const t = setTimeout(() => {
        if (drawerRef.current) {
          const first = drawerRef.current.querySelector(DRAWER_FOCUSABLE);
          if (first) first.focus();
        }
      }, 60);
      return () => clearTimeout(t);
    } else {
      if (prevFocusRef.current && typeof prevFocusRef.current.focus === 'function') {
        prevFocusRef.current.focus();
      }
      prevFocusRef.current = null;
    }
  }, [record?.id]);

  /* URL routing — push ?drawer=id so deep links, Browser Back, Browser Forward work */
  useEffectC(() => {
    if (!record) return;
    const url = new URL(window.location.href);
    url.searchParams.set('drawer', record.id);
    window.history.pushState({ nxDrawer: record.id }, '', url.toString());
    const onPop = () => { if (store.drawer) store.closeDrawer(); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      const u = new URL(window.location.href);
      u.searchParams.delete('drawer');
      window.history.replaceState(null, '', u.toString());
    };
  }, [record?.id]);

  /* Focus trap — cycles Tab/Shift+Tab within the drawer */
  const handleFocusTrap = (e) => {
    if (e.key !== 'Tab' || !drawerRef.current) return;
    const elements = Array.from(drawerRef.current.querySelectorAll(DRAWER_FOCUSABLE));
    if (!elements.length) return;
    const first = elements[0], last = elements[elements.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  /* Tab bar keyboard navigation — ArrowLeft / ArrowRight */
  const allTabs = [
    { id: 'overview' }, { id: 'activity' }, { id: 'notes' }, { id: 'tasks' },
    { id: 'emails' }, { id: 'opportunities' }, { id: 'files' },
    { id: 'timeline' }, { id: 'discuss' }, { id: 'ai' },
  ];
  const handleTabKeyNav = (e) => {
    const visibleTabs = drawerRef.current
      ? Array.from(drawerRef.current.querySelectorAll('[role="tab"]'))
      : [];
    const idx = visibleTabs.indexOf(document.activeElement);
    if (idx === -1) return;
    if (e.key === 'ArrowRight') { e.preventDefault(); visibleTabs[(idx + 1) % visibleTabs.length]?.focus(); }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); visibleTabs[(idx - 1 + visibleTabs.length) % visibleTabs.length]?.focus(); }
  };

  /* Deep link restore — fires once after store data has loaded, reads ?drawer=id from URL */
  const deepLinkDoneRef = useRefC(false);
  useEffectC(() => {
    if (deepLinkDoneRef.current || record) return;
    const all = [...(store.people || []), ...(store.companies || []), ...(store.deals || [])];
    if (!all.length) return;
    deepLinkDoneRef.current = true;
    const id = new URLSearchParams(window.location.search).get('drawer');
    if (!id) return;
    const found = all.find(r => r.id === id);
    if (found) store.openDrawer(found);
  }, [store.people, store.companies, store.deals, record, store.openDrawer]);

  /* Status popover — click-outside + Escape close. Runs unconditionally (hooks rule). */
  useEffectC(() => {
    if (!statusPopoverOpen) return;
    const onDoc = (e) => { if (statusPopoverRef.current && !statusPopoverRef.current.contains(e.target)) setStatusPopoverOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setStatusPopoverOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [statusPopoverOpen]);

  /* Tab strip scroll — show/hide chevron nav buttons */
  const checkTabScroll = () => {
    const el = tabsRef.current;
    if (!el) return;
    setTabScroll({
      canLeft:  el.scrollLeft > 1,
      canRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  };
  const scrollTabs = (dir) => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 120, behavior: 'smooth' });
  };
  useEffectC(() => {
    const el = tabsRef.current;
    if (!el) return;
    checkTabScroll();
    el.addEventListener('scroll', checkTabScroll, { passive: true });
    return () => el.removeEventListener('scroll', checkTabScroll);
  }, [record?.id]);
  useEffectC(() => {
    const el = tabsRef.current;
    if (!el) return;
    const active = el.querySelector('[aria-selected="true"]');
    if (!active) return;
    const elLeft   = el.scrollLeft;
    const elWidth  = el.clientWidth;
    const tabLeft  = active.offsetLeft;
    const tabRight = tabLeft + active.offsetWidth;
    if (tabLeft < elLeft + 4) el.scrollLeft = tabLeft - 4;
    else if (tabRight > elLeft + elWidth - 4) el.scrollLeft = tabRight - elWidth + 4;
    checkTabScroll();
  }, [tab]);

  /* File count — fetch per-record doc count when drawer opens so badge is accurate */
  const [fileCount, setFileCount] = useStateC(null);
  useEffectC(() => {
    if (!record?.id) return;
    setFileCount(null);
    const wsId = window.__nx_auth?.user?.activeWorkspaceId;
    const api  = window.__nx_api;
    if (!wsId || !api?.document?.list) { setFileCount(0); return; }
    const isTaskRec = 'done' in record;
    const param = isTaskRec
      ? { taskId:    record.id }
      : record.stageId !== undefined
        ? { dealId:    record.id }
        : record.domain !== undefined
          ? { companyId: record.id }
          : { personId:  record.id };
    let cancelled = false;
    api.document.list(wsId, { ...param, pageSize: 100 })
      .then(res => {
        if (!cancelled) {
          const raw = Array.isArray(res) ? res : (res?.data || res?.items || []);
          setFileCount(raw.length);
        }
      })
      .catch(() => { if (!cancelled) setFileCount(0); });
    return () => { cancelled = true; };
  }, [record?.id]);

  /* Custom fields gate — hide Fields tab when workspace has none for this entity type */
  const [customFieldCount, setCustomFieldCount] = useStateC(null);
  useEffectC(() => {
    if (!record?.id || 'done' in record) return; // tasks don't have a Fields tab
    const wsId = window.__nx_auth?.user?.activeWorkspaceId;
    const api  = window.__nx_api;
    if (!wsId || !api?.customFields?.getDefinitions) { setCustomFieldCount(0); return; }
    const et = record.stageId !== undefined ? 'deals' : record.domain !== undefined ? 'companies' : 'people';
    let cancelled = false;
    api.customFields.getDefinitions(wsId, et)
      .then(r => { if (!cancelled) setCustomFieldCount((Array.isArray(r) ? r : (r?.data ?? [])).length); })
      .catch(() => { if (!cancelled) setCustomFieldCount(0); });
    return () => { cancelled = true; };
  }, [record?.id]);
  useEffectC(() => {
    if (tab === 'fields' && customFieldCount === 0) setTab('overview');
  }, [tab, customFieldCount]);

  /* Description inline editor — auto-focus + cursor-to-end when entering edit mode */
  useEffectC(() => {
    if (!descEditing || !descTextareaRef.current) return;
    const el = descTextareaRef.current;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [descEditing]);

  const goBack = () => {
    const prev = window.__nx_drawer_back;
    window.__nx_drawer_back = null;
    if (prev) store.openDrawer(prev);
  };

  if (!record) return null;

  /* ── TASK DRAWER ─────────────────────────────────────────────
     Tasks are first-class records with a dedicated drawer. Home tab
     uses the same two-column nx-pov-layout as Company / Deal.
     Timeline / Files / Discuss are coming soon. */
  const isTask = 'done' in record;
  if (isTask) {
    const live           = record; // liveDrawer in state/index.jsx now resolves tasks reactively
    const todayStr       = new Date().toISOString().split('T')[0];
    const isOverdue      = !!(live.dueDate && live.dueDate < todayStr && !live.done && live.status !== 'cancelled' && !live.archived);
    const statusLabel    = live.done ? 'Completed' : live.status === 'cancelled' ? 'Cancelled' : live.status === 'in_progress' ? 'In Progress' : 'Open';
    const statusTone     = live.done ? 'green' : live.status === 'cancelled' ? 'red' : live.status === 'in_progress' ? 'blue' : 'gray';
    const priTone        = (live.priority === 'High' || live.priority === 'Urgent') ? 'red' : live.priority === 'Medium' ? 'amber' : 'gray';
    const heroIcon       = live.done ? 'CheckCircle' : live.status === 'cancelled' ? 'XCircle' : live.status === 'in_progress' ? 'Clock' : 'CircleDot';
    const heroBg         = live.done ? 'var(--color-success-50,#F0FDF4)' : live.status === 'cancelled' ? 'var(--color-danger-50,#FFF1F1)' : 'var(--nx-accent-tint)';
    const heroColor      = live.done ? 'var(--color-success-600,#059669)' : live.status === 'cancelled' ? 'var(--color-danger-600,#DC2626)' : 'var(--nx-accent)';
    const relatedPerson  = live.personId  ? store.people.find(p => p.id === live.personId)     : null;
    const relatedCompany = live.companyId ? store.companies.find(c => c.id === live.companyId)  : null;
    const relatedDeal    = live.dealId    ? (store.deals || []).find(d => d.id === live.dealId) : null;

    const fmtDate    = (v) => v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    const fmtDueDate = (v) => v ? new Date(v + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

    const taskOnDelete = () => store.confirm({
      title: 'Delete this task?',
      description: 'This action cannot be undone.',
      danger: true,
      confirmLabel: 'Delete',
      onConfirm: () => { store.remove('tasks', record.id); store.closeDrawer(); },
    });

    const taskOnArchive = () => store.confirm({
      title: 'Archive this task?',
      description: 'Archived tasks are permanently hidden from all views. This cannot be undone.',
      danger: true,
      confirmLabel: 'Archive',
      onConfirm: () => { store.archiveTask(live.id); store.closeDrawer(); },
    });

    const TASK_TABS = [
      { id: 'home',     label: 'Home' },
      { id: 'timeline', label: 'Timeline' },
      { id: 'files',    label: 'Files', count: fileCount },
      { id: 'discuss',  label: 'Discuss' },
    ];

    const showHome = tab === 'home' || (tab !== 'timeline' && tab !== 'files' && tab !== 'discuss');

    const dueDateStyle = isOverdue ? { color: 'var(--color-danger-600,#DC2626)', fontWeight: 500 } : {};

    const renderPriBadge = (priority) => {
      const iconMap = { Urgent: 'AlertTriangle', High: 'ArrowUp', Medium: 'Minus', Low: 'ArrowDown' };
      return (
        <span className={`nx-pri-badge nx-pri-${(priority || 'low').toLowerCase()}`}>
          <NexusIcon name={iconMap[priority] || 'Minus'} size={9} />
          {priority}
        </span>
      );
    };

    const currentStatusKey = live.done ? 'done' : live.status === 'cancelled' ? 'cancelled' : live.status === 'in_progress' ? 'in_progress' : 'open';

    const STATUS_OPTIONS = [
      { key: 'open',        label: 'Open',        icon: 'CircleDot',   color: 'var(--nx-text-muted)' },
      { key: 'in_progress', label: 'In Progress',  icon: 'Clock',       color: 'var(--nx-accent)' },
      { key: 'done',        label: 'Completed',    icon: 'CheckCircle', color: 'var(--color-success-600,#059669)' },
      { key: 'cancelled',   label: 'Cancelled',    icon: 'XCircle',     color: 'var(--color-danger-600,#DC2626)' },
    ];

    const handleStatusChange = (newKey) => {
      setStatusPopoverOpen(false);
      if (newKey === currentStatusKey) return;
      if (newKey === 'cancelled') {
        store.confirm({
          title: 'Cancel this task?',
          description: 'The task will be marked as cancelled. You can reopen it later.',
          confirmLabel: 'Cancel Task',
          danger: false,
          onConfirm: () => store.cancelTask(live.id),
        });
        return;
      }
      if (newKey === 'done')        { store.update('tasks', live.id, { done: true });  return; }
      if (newKey === 'in_progress') { store.startTask(live.id);                        return; }
      if (newKey === 'open')        { store.update('tasks', live.id, { done: false }); return; }
    };

    const handleDescCancel = () => {
      setDescEditing(false);
      setDescDraft('');
    };

    const handleDescSave = () => {
      if (descSaving) return;
      setDescSaving(true);
      store.update('tasks', live.id, { notes: descDraft });
      setDescEditing(false);
      setDescSaving(false);
    };

    return (
      <>
        <div className="nx-drawer-veil" role="presentation" aria-hidden="true" onClick={store.closeDrawer} />
        <aside
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="nx-drawer-title"
          onKeyDown={handleFocusTrap}
          className={'nx-drawer is-task' + (expanded ? ' is-expanded' : '')}
        >
          {/* ── HEAD ── */}
          <div className="nx-drawer-head">
            <div className="nx-drawer-head-top">
              <button type="button" className="nx-drawer-prev" aria-label="Close" onClick={store.closeDrawer} title="Close (Esc)">
                <NexusIcon name="X" size={14} />
              </button>
              {window.__nx_drawer_back && (
                <button type="button" className="nx-drawer-prev" aria-label="Back" onClick={goBack} title="Back">
                  <NexusIcon name="ChevronLeft" size={14} />
                </button>
              )}
              <div className="nx-drawer-breadcrumb">
                <span>Tasks</span>
                <NexusIcon name="ChevronRight" size={11} />
                <span className="is-current">{live.title}</span>
              </div>
              <span style={{ flex: 1 }} />
              <button type="button" className="nx-drawer-expand-btn"
                aria-label={expanded ? 'Collapse' : 'Expand workspace'}
                title={expanded ? 'Collapse' : 'Expand Workspace'}
                onClick={() => setExpanded(e => !e)}>
                <NexusIcon name={expanded ? 'Minimize2' : 'Maximize2'} size={13} />
              </button>
              <Btn variant="ghost" size="sm" icon="Edit" onClick={() => store.openEditForm('tasks', live)}>Edit</Btn>
              <DrawerMore onDelete={taskOnDelete} record={live} type="tasks"
                extraItems={null}
                dangerItems={({ close }) => (<>
                  {!live.archived && (
                    <button type="button" role="menuitem" className="nx-chip-menu-item is-danger" onClick={() => { close(); taskOnArchive(); }}><NexusIcon name="Archive" size={11} /><span>Archive</span></button>
                  )}
                </>)}
              />
            </div>

            {/* Hero — mirrors Company/Deal identity block */}
            <div className="nx-drawer-hero is-task">
              <div className="nx-phero-id-row">
                {/* Status chip — clickable, opens status popover */}
                <span ref={statusPopoverRef} style={{ position: 'relative', flexShrink: 0 }}>
                  <button
                    type="button"
                    className="nx-task-status-chip"
                    aria-label={`Status: ${statusLabel} — click to change`}
                    aria-haspopup="listbox"
                    aria-expanded={statusPopoverOpen}
                    style={{ background: heroBg, color: heroColor, opacity: live.archived ? 0.5 : 1, cursor: live.archived ? 'default' : 'pointer' }}
                    onClick={live.archived ? undefined : () => setStatusPopoverOpen(o => !o)}
                  >
                    <NexusIcon name={heroIcon} size={22} />
                  </button>
                  {statusPopoverOpen && !live.archived && (
                    <div className="nx-status-popover" role="listbox" aria-label="Change task status">
                      {STATUS_OPTIONS.map(opt => (
                        <button
                          type="button"
                          key={opt.key}
                          role="option"
                          aria-selected={currentStatusKey === opt.key}
                          className={'nx-status-option' + (currentStatusKey === opt.key ? ' is-active' : '')}
                          onClick={() => handleStatusChange(opt.key)}
                        >
                          <NexusIcon name={opt.icon} size={13} style={{ color: opt.color, flexShrink: 0 }} />
                          <span>{opt.label}</span>
                          {currentStatusKey === opt.key && <NexusIcon name="Check" size={11} style={{ marginLeft: 'auto', color: 'var(--nx-accent)' }} />}
                        </button>
                      ))}
                    </div>
                  )}
                </span>
                <div className="nx-drawer-hero-meta">
                  <h2 id="nx-drawer-title" className="nx-drawer-title"
                    style={live.done ? { textDecoration: 'line-through', opacity: 0.65 } : live.status === 'cancelled' ? { opacity: 0.55 } : {}}>
                    {live.title}
                  </h2>
                  <span className="nx-drawer-sub">Task{relatedCompany ? ' · ' + relatedCompany.name : relatedDeal ? ' · ' + relatedDeal.title : ''}</span>
                  <div className="nx-drawer-pills">
                    <Pill tone={statusTone}>{statusLabel}</Pill>
                    {renderPriBadge(live.priority)}
                    {live.archived && <Pill tone="amber">Archived</Pill>}
                    {isOverdue    && <Pill tone="red">Overdue</Pill>}
                  </div>
                </div>

                {/* Primary action — state-aware, right side of hero row */}
                {!live.archived && (
                  <div className="nx-task-hero-action">
                    {!live.done && live.status !== 'cancelled' && (
                      <Btn variant="primary" size="sm" icon="Check"
                        onClick={() => store.update('tasks', live.id, { done: true })}>
                        Mark Complete
                      </Btn>
                    )}
                    {(live.done || live.status === 'cancelled') && (
                      <Btn variant="ghost" size="sm" icon="RotateCcw"
                        onClick={() => store.update('tasks', live.id, { done: false })}>
                        Reopen
                      </Btn>
                    )}
                  </div>
                )}
              </div>

              {/* Identity chips: due date + assignee — mirrors Company domain/location chips */}
              <div className="nx-phero-identity-block">
                <div className="nx-phero-identity-item" style={dueDateStyle}>
                  <NexusIcon name="Calendar" size={12} />
                  <span>{live.dueDate ? fmtDueDate(live.dueDate) : 'No due date'}{isOverdue ? ' · Overdue' : ''}</span>
                </div>
                {live.assignee
                  ? <div className="nx-phero-identity-item">
                      <NexusIcon name="User" size={12} />
                      <span>{live.assignee}</span>
                    </div>
                  : <div className="nx-phero-identity-item" style={{ opacity: 0.45 }}>
                      <NexusIcon name="User" size={12} />
                      <span>Unassigned</span>
                    </div>
                }
              </div>
            </div>

            {/* Tab bar — Timeline/Files/Discuss are disabled until features ship */}
            <div className="nx-drawer-tabs" role="tablist" onKeyDown={handleTabKeyNav}>
              {TASK_TABS.map(t => {
                const isComing = false;
                const isActive = !isComing && (showHome ? t.id === 'home' : tab === t.id);
                return (
                  <button
                    type="button"
                    key={t.id}
                    role="tab"
                    aria-selected={isActive}
                    aria-disabled={isComing || undefined}
                    tabIndex={isComing ? -1 : undefined}
                    title={isComing ? 'Coming in a future release' : undefined}
                    className={'nx-drawer-tab' + (isActive ? ' is-active' : '') + (isComing ? ' is-coming' : '')}
                    onClick={isComing ? undefined : () => setTab(t.id)}
                    style={isComing ? { opacity: 0.4, cursor: 'default', pointerEvents: 'none' } : undefined}
                  >
                    <span>{t.label}</span>
                    {t.count != null && <span className="nx-tab-count">{t.count}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── BODY ── */}
          <div className="nx-drawer-body">
            {showHome && (
              <div className="nx-drawer-overview" style={{ padding: 0 }}>
                <div className="nx-pov-layout">

                  {/* ── MAIN column ── */}
                  <div className="nx-pov-main">

                    {/* Description — inline editable; Edit in panel header opens textarea in-place */}
                    <DrawerPanel title="Description"
                      actions={!descEditing && (
                        <button type="button" className="nx-panel-link"
                          onClick={() => { setDescDraft(live.notes || ''); setDescEditing(true); }}>
                          <NexusIcon name="Edit" size={10} /> Edit
                        </button>
                      )}>
                      {descEditing ? (
                        <>
                          <textarea
                            ref={descTextareaRef}
                            className="nx-input nx-textarea nx-desc-textarea"
                            value={descDraft}
                            placeholder="Add a description…"
                            rows={5}
                            disabled={descSaving}
                            onChange={e => setDescDraft(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Escape') { e.stopPropagation(); handleDescCancel(); }
                              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleDescSave();
                            }}
                          />
                          <div className="nx-desc-editor-actions">
                            <button type="button" className="nx-btn nx-btn-ghost nx-btn-sm"
                              disabled={descSaving} onClick={handleDescCancel}>
                              Cancel
                            </button>
                            <button type="button" className="nx-btn nx-btn-primary nx-btn-sm"
                              disabled={descSaving} onClick={handleDescSave}>
                              {descSaving ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        </>
                      ) : (
                        live.notes
                          ? <p className="nx-desc-body">{live.notes}</p>
                          : <div className="nx-desc-empty">
                              <NexusIcon name="AlignLeft" size={13} />
                              <span>No description added yet.</span>
                            </div>
                      )}
                    </DrawerPanel>

                    {/* Related Records — three labelled sections, each with its own empty state */}
                    <DrawerPanel title="Related Records">
                      <div className="nx-prelated-section">
                        <div className="nx-prelated-label">Company</div>
                        {relatedCompany
                          ? <button type="button" className="nx-prelated-row"
                              onClick={() => { window.__nx_drawer_back = live; store.openDrawer(relatedCompany); }}>
                              <span className="nx-prelated-icon" style={{ background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)' }}>
                                <NexusIcon name="Building2" size={12} />
                              </span>
                              <div className="nx-prelated-body">
                                <span className="nx-prelated-title">{relatedCompany.name}</span>
                                <span className="nx-prelated-sub">{[relatedCompany.industry, relatedCompany.location].filter(Boolean).join(' · ')}</span>
                              </div>
                              <NexusIcon name="ArrowUpRight" size={11} style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
                            </button>
                          : <button type="button" className="nx-prelated-row nx-prelated-row--empty"
                              onClick={() => store.openEditForm('tasks', live)}>
                              <span className="nx-prelated-icon" style={{ background: 'var(--nx-surface-alt)', color: 'var(--nx-text-faint)' }}>
                                <NexusIcon name="Plus" size={12} />
                              </span>
                              <div className="nx-prelated-body">
                                <span className="nx-prelated-title" style={{ fontWeight: 500, color: 'var(--nx-text-muted)' }}>Link Company</span>
                              </div>
                            </button>
                        }
                      </div>
                      <div className="nx-prelated-section">
                        <div className="nx-prelated-label">Contact</div>
                        {relatedPerson
                          ? <button type="button" className="nx-prelated-row"
                              onClick={() => { window.__nx_drawer_back = live; store.openDrawer(relatedPerson); }}>
                              <Avatar name={relatedPerson.name} tone="var(--color-primary-500)" size={26} style={{ flexShrink: 0 }} />
                              <div className="nx-prelated-body">
                                <span className="nx-prelated-title">{relatedPerson.name}</span>
                                <span className="nx-prelated-sub">{[relatedPerson.role, relatedPerson.company].filter(Boolean).join(' · ')}</span>
                              </div>
                              <NexusIcon name="ArrowUpRight" size={11} style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
                            </button>
                          : <button type="button" className="nx-prelated-row nx-prelated-row--empty"
                              onClick={() => store.openEditForm('tasks', live)}>
                              <span className="nx-prelated-icon" style={{ background: 'var(--nx-surface-alt)', color: 'var(--nx-text-faint)' }}>
                                <NexusIcon name="Plus" size={12} />
                              </span>
                              <div className="nx-prelated-body">
                                <span className="nx-prelated-title" style={{ fontWeight: 500, color: 'var(--nx-text-muted)' }}>Link Contact</span>
                              </div>
                            </button>
                        }
                      </div>
                      <div className="nx-prelated-section">
                        <div className="nx-prelated-label">Deal</div>
                        {relatedDeal
                          ? <button type="button" className="nx-prelated-row"
                              onClick={() => { window.__nx_drawer_back = live; store.openDrawer(relatedDeal); }}>
                              <span className="nx-prelated-icon" style={{ background: 'var(--nx-surface-alt)', color: 'var(--nx-accent)' }}>
                                <NexusIcon name="Target" size={12} />
                              </span>
                              <div className="nx-prelated-body">
                                <span className="nx-prelated-title">{relatedDeal.title}</span>
                                <span className="nx-prelated-sub">${(relatedDeal.value || 0).toLocaleString()}</span>
                              </div>
                              <NexusIcon name="ArrowUpRight" size={11} style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
                            </button>
                          : <button type="button" className="nx-prelated-row nx-prelated-row--empty"
                              onClick={() => store.openEditForm('tasks', live)}>
                              <span className="nx-prelated-icon" style={{ background: 'var(--nx-surface-alt)', color: 'var(--nx-text-faint)' }}>
                                <NexusIcon name="Plus" size={12} />
                              </span>
                              <div className="nx-prelated-body">
                                <span className="nx-prelated-title" style={{ fontWeight: 500, color: 'var(--nx-text-muted)' }}>Link Deal</span>
                              </div>
                            </button>
                        }
                      </div>
                    </DrawerPanel>

                    {/* Custom Fields — CF-05: Task record detail integration (CF-00 P.4) */}
                    <DrawerPanel title="Custom Fields">
                      {window.CustomFieldsPanel
                        ? React.createElement(window.CustomFieldsPanel, { record: live, entityType: 'tasks' })
                        : <span className="nx-muted nx-mini" style={{ padding: '8px 0', display: 'block' }}>Custom fields not available.</span>
                      }
                    </DrawerPanel>

                  </div>

                  {/* ── SIDE column ── */}
                  <div className="nx-pov-side">

                    {/* Details — Status + Priority + Due Date + Assignee (properties editor view) */}
                    <DrawerPanel title="Details">
                      <div className="nx-crm-snapshot">
                        <div className="nx-crm-row">
                          <span className="nx-crm-lbl">Status</span>
                          <Pill tone={statusTone}>{statusLabel}</Pill>
                        </div>
                        <div className="nx-crm-row">
                          <span className="nx-crm-lbl">Priority</span>
                          {renderPriBadge(live.priority)}
                        </div>
                        <div className="nx-crm-row">
                          <span className="nx-crm-lbl">Due Date</span>
                          <span className="nx-crm-val" style={dueDateStyle}>
                            {live.dueDate ? fmtDueDate(live.dueDate) : '—'}
                          </span>
                        </div>
                        <div className="nx-crm-row">
                          <span className="nx-crm-lbl">Assignee</span>
                          {live.assignee
                            ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Avatar name={live.assignee} tone="var(--color-primary-500)" size={18} />
                                <span className="nx-crm-val">{live.assignee}</span>
                              </span>
                            : <span className="nx-crm-val" style={{ color: 'var(--nx-text-muted)' }}>Unassigned</span>
                          }
                        </div>
                        {live.done && live.completedAt && (
                          <div className="nx-crm-row">
                            <span className="nx-crm-lbl">Completed</span>
                            <span className="nx-crm-val">{fmtDate(live.completedAt)}</span>
                          </div>
                        )}
                      </div>
                    </DrawerPanel>

                    {/* Record Metadata — UUID on single line with copy button */}
                    <DrawerPanel title="Record Metadata">
                      <div className="nx-crm-snapshot">
                        <div className="nx-crm-row" style={{ minWidth: 0 }}>
                          <span className="nx-crm-lbl">Record ID</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <code style={{ fontFamily: 'monospace', fontSize: 10.5, color: 'var(--nx-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{live.id}</code>
                            <button type="button"
                              style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: 'var(--nx-text-faint)', flexShrink: 0, display: 'flex', alignItems: 'center', lineHeight: 1 }}
                              title="Copy Record ID"
                              onClick={() => navigator.clipboard?.writeText(live.id).catch(() => {})}>
                              <NexusIcon name="Copy" size={11} />
                            </button>
                          </span>
                        </div>
                        <div className="nx-crm-row"><span className="nx-crm-lbl">Created</span><span className="nx-crm-val">{fmtDate(live.createdAt)}</span></div>
                        <div className="nx-crm-row"><span className="nx-crm-lbl">Modified</span><span className="nx-crm-val">{live.updatedAt ? fmtDate(live.updatedAt) : '—'}</span></div>
                      </div>
                    </DrawerPanel>

                  </div>
                </div>
              </div>
            )}
            {!showHome && tab === 'timeline' && (window.CustomerTimelineView
              ? React.createElement(window.CustomerTimelineView, { record: live, type: 'task' })
              : <span className="nx-muted nx-mini" style={{ padding: 16, display: 'block' }}>Loading…</span>)}
            {!showHome && tab === 'files' && <DocumentsTab record={live} type="tasks" onCountChange={setFileCount} />}
            {!showHome && tab === 'discuss' && (window.TeamDiscussionPanel
              ? React.createElement(window.TeamDiscussionPanel, { record: live, type: 'tasks' })
              : <span className="nx-muted nx-mini" style={{ padding: 16, display: 'block' }}>Loading…</span>)}
          </div>
        </aside>
      </>
    );
  }
  /* ── END TASK DRAWER ──────────────────────────────────────── */

  const isDeal    = record.stageId  !== undefined;
  const isCompany = !isDeal && record.domain !== undefined;
  const isPerson  = !isDeal && !isCompany;
  const type      = isPerson ? 'people' : isCompany ? 'companies' : 'deals';

  const breadcrumbLabel = isPerson ? 'People' : isCompany ? 'Companies' : (() => {
    try {
      const wsId = window.__nx_auth?.user?.activeWorkspaceId;
      const base = wsId ? '/app/' + wsId + '/' : '';
      const seg  = base ? window.location.pathname.slice(base.length).split('/')[0] : '';
      return { deals: 'Deals', people: 'People', companies: 'Companies', tasks: 'Tasks' }[seg] || 'Deals';
    } catch { return 'Deals'; }
  })();

  const title = record.name || record.title;
  const subtitle = isPerson  ? `${record.role || ''} · ${record.company || ''}`
                : isCompany  ? `${record.industry || ''} · ${record.location || ''}`
                : isDeal     ? `Deal · ${store.companies.find(c => c.id === record.companyId)?.name || '—'}`
                : '';

  const onDelete = () => {
    store.confirm({
      title: 'Delete this ' + (isPerson ? 'person' : isCompany ? 'company' : 'deal') + '?',
      description: 'This action cannot be undone. Linked records will be cleaned up.',
      danger: true,
      onConfirm: () => { store.remove(type, record.id); store.closeDrawer(); },
    });
  };

  const docCount = store.documents.filter(d => (isPerson && d.personId === record.id) || (isCompany && d.companyId === record.id) || (isDeal && d.dealId === record.id)).length;
  const recordActivities = store.activity.filter(a =>
    isPerson  ? a.personId  === record.id :
    isCompany ? a.companyId === record.id :
    isDeal    ? a.dealId    === record.id :
    false
  );
  const counts = {
    activity: recordActivities.length,
    notes:    store.notes.filter(n => isPerson ? n.personId === record.id : isCompany ? n.companyId === record.id : isDeal ? n.dealId === record.id : false).length,
    tasks:    store.tasks.filter(t => isPerson ? t.personId === record.id : isCompany ? t.companyId === record.id : isDeal ? t.dealId === record.id : true).length,
    emails:   recordActivities.filter(a => a.type === 'email').length,
    files:    fileCount ?? docCount,
  };

  return (
    <>
      <div className="nx-drawer-veil" role="presentation" aria-hidden="true" onClick={store.closeDrawer} />
      <aside
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="nx-drawer-title"
        onKeyDown={handleFocusTrap}
        className={'nx-drawer' + (isPerson ? ' is-person' : '') + (isCompany ? ' is-company' : '') + (isDeal ? ' is-deal' : '') + (expanded ? ' is-expanded' : '')}
      >
        <div className="nx-drawer-head">
          <div className="nx-drawer-head-top">
            <button type="button" className="nx-drawer-prev" aria-label="Close" onClick={store.closeDrawer} title="Close (Esc)"><NexusIcon name="X" size={14} /></button>
            {window.__nx_drawer_back && (
              <button type="button" className="nx-drawer-prev" aria-label="Back" onClick={goBack} title="Back">
                <NexusIcon name="ChevronLeft" size={14} />
              </button>
            )}
            <div className="nx-drawer-breadcrumb">
              <span>{breadcrumbLabel}</span>
              <NexusIcon name="ChevronRight" size={11} />
              <span className="is-current">{title}</span>
            </div>
            <span style={{ flex: 1 }} />
            {(isPerson || isCompany || isDeal) && (
              <button type="button" className="nx-drawer-expand-btn" aria-label={expanded ? 'Collapse' : 'Expand workspace'} title={expanded ? 'Collapse' : 'Expand Workspace'} onClick={() => setExpanded(e => !e)}>
                <NexusIcon name={expanded ? 'Minimize2' : 'Maximize2'} size={13} />
              </button>
            )}
            {(!isDeal || ((window.__nx_rbac?.can('crm.deals.edit') || window.__nx_rbac?.can('crm.manage')) ?? true)) && (
              <Btn variant="ghost" size="sm" icon="Edit" onClick={() => store.openEditForm(type, record)}>Edit</Btn>
            )}
            <DrawerMore onDelete={onDelete} record={record} type={type} />
          </div>

          <div className={'nx-drawer-hero' + (isPerson ? ' is-person' : '') + (isCompany ? ' is-company' : '') + (isDeal ? ' is-deal' : '')}>
            {isPerson ? (
              <>
                <div className="nx-phero-id-row">
                  <Avatar name={record.name} tone={record.tone} size={44} />
                  <div className="nx-drawer-hero-meta">
                    <h2 id="nx-drawer-title" className="nx-drawer-title">{record.salutation ? `${record.salutation} ${title}` : title}</h2>
                    <span className="nx-drawer-sub">{record.role}</span>
                    {record.company && (
                      <span className="nx-phero-company">
                        <NexusIcon name="Building2" size={10} />
                        {record.company}
                      </span>
                    )}
                    <div className="nx-drawer-pills">
                      <Pill tone="blue">{record.status}</Pill>
                      <Score value={record.score} />
                    </div>
                  </div>
                </div>
                {window.PersonContactRow && React.createElement(window.PersonContactRow, { record })}
                {window.PersonCRMSummary && React.createElement(window.PersonCRMSummary, { record })}
                <div className="nx-phero-action-bar">
                  {[
                    { icon:'Phone',         label:'Call',    fn: () => { setTab('activity'); setActivityIntent({ type: 'call' }); } },
                    { icon:'MessageCircle', label:'Message', fn: () => { setTab('activity'); setActivityIntent({ type: 'whatsapp' }); } },
                    { icon:'Mail',          label:'Email',   fn: () => { setTab('emails'); setEmailAutoCompose(true); } },
                    { icon:'Calendar',      label:'Meeting', fn: () => { setTab('activity'); setActivityIntent({ type: 'meeting' }); } },
                    { icon:'CheckSquare',   label:'Task',    fn: () => store.openCreateForm('tasks', { personId: record.id, companyId: record.companyId || null, assignee: record.owner || '', _context: { source: 'person', readOnly: ['personId', 'companyId', 'assignee'], personId: record.id } }) },
                    { icon:'StickyNote',    label:'Note',    fn: () => setTab('notes'), primary: true },
                  ].map(a => (
                    <button key={a.label}
                      type="button"
                      aria-label={a.label}
                      className={'nx-phero-act' + (a.primary ? ' is-primary' : '')}
                      onClick={a.fn} title={a.label}>
                      <NexusIcon name={a.icon} size={14} />
                      <span>{a.label}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : isCompany ? (
              <>
                {/* ── Company hero: column layout matching Person ── */}
                <div className="nx-phero-id-row">
                  <span className="nx-domain-mark" style={{ width: 44, height: 44, fontSize: 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{(record.name || '?')[0].toUpperCase()}</span>
                  <div className="nx-drawer-hero-meta">
                    <h2 id="nx-drawer-title" className="nx-drawer-title">{title}</h2>
                    <span className="nx-drawer-sub">{record.industry}{record.location ? ' · ' + record.location : ''}</span>
                    <div className="nx-drawer-pills">
                      <Pill tone="purple">{record.lifecycle}</Pill>
                      <Score value={record.score} />
                    </div>
                  </div>
                </div>
                {/* Contact row: domain, location, parent enterprise */}
                <div className="nx-phero-identity-block">
                  {record.domain   && <div className="nx-phero-identity-item"><NexusIcon name="Globe"  size={12} /><span>{record.domain}</span></div>}
                  {record.location && <div className="nx-phero-identity-item"><NexusIcon name="MapPin" size={12} /><span>{record.location}</span></div>}
                  {record.employees && <div className="nx-phero-identity-item"><NexusIcon name="Users"  size={12} /><span>{record.employees.toLocaleString()} employees</span></div>}
                  {record.lifecycle === 'Account' && record.parentCompanyId && (() => {
                    const parent = store.companies.find(c => c.id === record.parentCompanyId);
                    return parent ? (
                      <div className="nx-phero-identity-item">
                        <NexusIcon name="Building" size={12} />
                        <button type="button" className="nx-link-plain" onClick={() => { window.__nx_drawer_back = record; store.openDrawer(parent); }}>{parent.name}</button>
                      </div>
                    ) : null;
                  })()}
                </div>
                {/* CRM strip */}
                <div className="nx-phero-crm-strip">
                  <div className="nx-pcs-cell"><Pill tone="purple">{record.lifecycle}</Pill></div>
                  <div className="nx-pcs-div" />
                  <div className="nx-pcs-cell">
                    <NexusIcon name="TrendingUp" size={11} />
                    <span className="nx-pcs-val">{record.score != null ? record.score : '—'}</span>
                    {record.score != null && <span className="nx-pcs-lbl">{getScoreBand(record.score)?.label ?? ''}</span>}
                  </div>
                  <div className="nx-pcs-div" />
                  <div className="nx-pcs-cell">
                    <NexusIcon name="User" size={11} />
                    <span className="nx-pcs-val">{(record.owner || '').split(' ')[0]}</span>
                  </div>
                  <div className="nx-pcs-div" />
                  <div className="nx-pcs-cell">
                    <NexusIcon name="Clock" size={11} />
                    <span className="nx-pcs-val">{record.lastActivity || '—'}</span>
                  </div>
                </div>
                {/* Labeled action bar */}
                <div className="nx-phero-action-bar">
                  {[
                    { icon:'Phone',         label:'Call',    fn: () => { setTab('activity'); setActivityIntent({ type: 'call' }); } },
                    { icon:'MessageCircle', label:'Message', fn: () => { setTab('activity'); setActivityIntent({ type: 'whatsapp' }); } },
                    { icon:'Mail',          label:'Email',   fn: () => { setTab('emails'); setEmailAutoCompose(true); } },
                    { icon:'Calendar',      label:'Meeting', fn: () => { setTab('activity'); setActivityIntent({ type: 'meeting' }); } },
                    { icon:'CheckSquare',   label:'Task',    fn: () => store.openCreateForm('tasks', { companyId: record.id }) },
                    { icon:'StickyNote',    label:'Note',    fn: () => setTab('notes'), primary: true },
                  ].map(a => (
                    <button key={a.label}
                      type="button"
                      aria-label={a.label}
                      className={'nx-phero-act' + (a.primary ? ' is-primary' : '')}
                      onClick={a.fn} title={a.label}>
                      <NexusIcon name={a.icon} size={14} />
                      <span>{a.label}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : isDeal ? (
              <>
                {/* ── Deal hero: column layout matching Person/Company ── */}
                <div className="nx-phero-id-row">
                  <span className="nx-deal-hero-mark" style={{ flexShrink: 0 }}>
                    <NexusIcon name="Target" size={20} />
                  </span>
                  <div className="nx-drawer-hero-meta">
                    <h2 id="nx-drawer-title" className="nx-drawer-title">{title}</h2>
                    <span className="nx-drawer-sub">
                      {'Deal'}
                      {(() => { const c = store.companies.find(c => c.id === record.companyId); return c ? ' · ' + c.name : ''; })()}
                    </span>
                    <div className="nx-drawer-pills">
                      <Pill tone={_stages.find(s => s.id === record.stageId)?.kind === 'WON' ? 'green' : _stages.find(s => s.id === record.stageId)?.kind === 'LOST' ? 'red' : 'blue'}>
                        {_stages.find(s => s.id === record.stageId)?.name}
                      </Pill>
                      <span className="nx-cell is-mono" style={{ fontWeight: 800, color: 'var(--nx-accent)' }}>${record.value.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                {/* Identity chips: company, close date, priority */}
                <div className="nx-phero-identity-block">
                  {store.companies.find(c => c.id === record.companyId) && (
                    <div className="nx-phero-identity-item">
                      <NexusIcon name="Building2" size={12} />
                      <span>{store.companies.find(c => c.id === record.companyId)?.name}</span>
                    </div>
                  )}
                  {record.closeDate && (
                    <div className="nx-phero-identity-item">
                      <NexusIcon name="Calendar" size={12} />
                      <span>Close {record.closeDate}</span>
                    </div>
                  )}
                  {record.priority && (
                    <div className="nx-phero-identity-item">
                      <NexusIcon name="AlertCircle" size={12} />
                      <span>{record.priority} priority</span>
                    </div>
                  )}
                </div>
                {/* CRM strip */}
                <div className="nx-phero-crm-strip">
                  <div className="nx-pcs-cell">
                    <Pill tone={_stages.find(s => s.id === record.stageId)?.kind === 'WON' ? 'green' : _stages.find(s => s.id === record.stageId)?.kind === 'LOST' ? 'red' : 'blue'}>
                      {_stages.find(s => s.id === record.stageId)?.name}
                    </Pill>
                  </div>
                  <div className="nx-pcs-div" />
                  <div className="nx-pcs-cell">
                    <NexusIcon name="DollarSign" size={11} />
                    <span className="nx-pcs-val" style={{ color: 'var(--nx-accent)', fontWeight: 800 }}>${record.value >= 1000 ? (record.value/1000).toFixed(1)+'K' : record.value}</span>
                  </div>
                  <div className="nx-pcs-div" />
                  <div className="nx-pcs-cell">
                    <NexusIcon name="User" size={11} />
                    <span className="nx-pcs-val">{(record.owner || '').split(' ')[0]}</span>
                  </div>
                  <div className="nx-pcs-div" />
                  <div className="nx-pcs-cell">
                    <NexusIcon name="Clock" size={11} />
                    <span className="nx-pcs-val">{record.age || 0}d old</span>
                  </div>
                </div>
                {/* Labeled action bar */}
                <div className="nx-phero-action-bar">
                  {[
                    { icon:'Phone',         label:'Call',    fn: () => { setTab('activity'); setActivityIntent({ type: 'call' }); } },
                    { icon:'Mail',          label:'Email',   fn: () => { setTab('emails'); setEmailAutoCompose(true); } },
                    { icon:'Calendar',      label:'Meeting', fn: () => { setTab('activity'); setActivityIntent({ type: 'meeting' }); } },
                    { icon:'CheckSquare',   label:'Task',    fn: () => store.openCreateForm('tasks', { dealId: record.id }) },
                    { icon:'StickyNote',    label:'Note',    fn: () => setTab('notes'), primary: true },
                  ].map(a => (
                    <button key={a.label}
                      type="button"
                      aria-label={a.label}
                      className={'nx-phero-act' + (a.primary ? ' is-primary' : '')}
                      onClick={a.fn} title={a.label}>
                      <NexusIcon name={a.icon} size={14} />
                      <span>{a.label}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <></>
            )}
          </div>


          <div className="nx-tab-strip-wrap">
            <button type="button" className={'nx-tab-nav-btn' + (tabScroll.canLeft ? '' : ' is-hidden')} onClick={() => scrollTabs(-1)} aria-label="Scroll tabs left" tabIndex={-1}>
              <NexusIcon name="ChevronLeft" size={12} />
            </button>
            <div className="nx-drawer-tabs" ref={tabsRef} role="tablist" onKeyDown={handleTabKeyNav}>
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'activity', label: 'Activity', count: counts.activity },
                { id: 'notes',    label: 'Notes',    count: counts.notes },
                { id: 'tasks',    label: 'Tasks',    count: counts.tasks },
                { id: 'emails',   label: 'Emails',   count: counts.emails },
                ...(isPerson  ? [{ id: 'opportunities', label: 'Deals', count: store.deals.filter(d => d.contactId === record.id).length }] : []),
                ...(isCompany ? [{ id: 'opportunities', label: 'Opportunities', count: store.deals.filter(d => d.companyId === record.id).length }] : []),
                { id: 'files',    label: 'Files',    count: counts.files },
                ...(isDeal && customFieldCount > 0 ? [{ id: 'fields', label: 'Fields' }] : []),
                { id: 'timeline', label: 'Timeline' },
                { id: 'discuss',  label: 'Discuss' },
                { id: 'ai',       label: 'AI' },
                ...(isDeal ? [
                  { id: 'committee',   label: 'Committee' },
                  { id: 'products',    label: 'Products' },
                  { id: 'checklist',   label: 'Checklist' },
                  { id: 'competitors', label: 'Competitors' },
                  { id: 'insight',     label: 'Insight' },
                  { id: 'history',     label: 'History' },
                ] : []),
              ].map(t => (
                <button type="button" key={t.id} role="tab" aria-selected={tab === t.id} className={'nx-drawer-tab' + (tab === t.id ? ' is-active' : '')} onClick={() => setTab(t.id)}>
                  <span>{t.label}</span>
                  {t.count != null && <span className="nx-tab-count">{t.count}</span>}
                </button>
              ))}
            </div>
            <button type="button" className={'nx-tab-nav-btn' + (tabScroll.canRight ? '' : ' is-hidden')} onClick={() => scrollTabs(1)} aria-label="Scroll tabs right" tabIndex={-1}>
              <NexusIcon name="ChevronRight" size={12} />
            </button>
          </div>
        </div>

        <div className="nx-drawer-body">
          {tab === 'overview' && (
            isPerson && window.PersonOverviewTab
              ? React.createElement(window.PersonOverviewTab, { record, type, setTab })
              : isCompany && window.CompanyOverviewTab
              ? React.createElement(window.CompanyOverviewTab, { record, type, setTab })
              : isDeal && window.DealOverviewTab
              ? React.createElement(window.DealOverviewTab, { record, type, setTab })
              : <OverviewTab record={record} type={type} isPerson={isPerson} isCompany={isCompany} isDeal={isDeal} setTab={setTab} />
          )}
          {tab === 'opportunities' && isPerson && window.PersonOpportunitiesTab && React.createElement(window.PersonOpportunitiesTab, { record, store })}
          {tab === 'opportunities' && isCompany && <CompanyOpportunitiesTab record={record} />}
          {tab === 'activity' && <ActivityTab record={record} intent={activityIntent} onConsumeIntent={() => setActivityIntent(null)} />}
          {tab === 'notes'    && <NotesTab record={record} />}
          {tab === 'tasks'    && <DrawerTasksTab record={record} />}
          {tab === 'emails'   && <EmailsTab record={record} autoCompose={emailAutoCompose} onConsumeCompose={() => setEmailAutoCompose(false)} />}
          {tab === 'files'    && <DocumentsTab record={record} type={type} onCountChange={setFileCount} />}
          {isDeal && tab === 'fields'   && (
            <div className="nx-drawer-page">
              {window.CustomFieldsPanel
                ? React.createElement(window.CustomFieldsPanel, { record, entityType: type })
                : <span className="nx-muted nx-mini" style={{ padding: 16, display: 'block' }}>Custom fields not available.</span>
              }
            </div>
          )}
          {isDeal && tab === 'committee'   && (window.BuyingCommitteeTab
            ? React.createElement(window.BuyingCommitteeTab,   { record, dealId: record.id, wsId: window.__nx_auth?.user?.activeWorkspaceId })
            : <span className="nx-muted nx-mini" style={{ padding: 16, display: 'block' }}>Loading…</span>)}
          {isDeal && tab === 'products'    && (window.DealProductsTab
            ? React.createElement(window.DealProductsTab,     { record, dealId: record.id, wsId: window.__nx_auth?.user?.activeWorkspaceId })
            : <span className="nx-muted nx-mini" style={{ padding: 16, display: 'block' }}>Loading…</span>)}
          {isDeal && tab === 'checklist'   && (window.DealChecklistTab
            ? React.createElement(window.DealChecklistTab,    { record, dealId: record.id, wsId: window.__nx_auth?.user?.activeWorkspaceId })
            : <span className="nx-muted nx-mini" style={{ padding: 16, display: 'block' }}>Loading…</span>)}
          {isDeal && tab === 'competitors' && (window.CompetitorsTab
            ? React.createElement(window.CompetitorsTab,      { record, dealId: record.id, wsId: window.__nx_auth?.user?.activeWorkspaceId })
            : <span className="nx-muted nx-mini" style={{ padding: 16, display: 'block' }}>Loading…</span>)}
          {isDeal && tab === 'insight'     && (window.DealIntelligenceTab
            ? React.createElement(window.DealIntelligenceTab, { record, dealId: record.id, wsId: window.__nx_auth?.user?.activeWorkspaceId })
            : <span className="nx-muted nx-mini" style={{ padding: 16, display: 'block' }}>Loading…</span>)}
          {isDeal && tab === 'history'    && (window.StageHistoryPanel
            ? React.createElement(window.StageHistoryPanel,   { record, stages: store.pipeline || [] })
            : <span className="nx-muted nx-mini" style={{ padding: 16, display: 'block' }}>Loading…</span>)}
          {tab === 'timeline' && (window.CustomerTimelineView
            ? React.createElement(window.CustomerTimelineView, { record, type: isPerson ? 'person' : isCompany ? 'company' : 'deal' })
            : <span className="nx-muted nx-mini" style={{ padding: 16, display: 'block' }}>Loading…</span>)}
          {tab === 'discuss'  && (window.TeamDiscussionPanel
            ? React.createElement(window.TeamDiscussionPanel,  { record, type })
            : <span className="nx-muted nx-mini" style={{ padding: 16, display: 'block' }}>Loading…</span>)}
          {tab === 'ai' && (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'56px 24px', gap:16, textAlign:'center' }}>
              <div style={{ width:56, height:56, borderRadius:16, background:'var(--nx-surface-alt)', border:'1px solid var(--nx-divider)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <NexusIcon name="Sparkles" size={26} style={{ color:'var(--nx-text-muted)', opacity:0.6 }} />
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                <div style={{ fontSize:15, fontWeight:600, color:'var(--nx-text)' }}>Enterprise AI Workspace</div>
                <div style={{ fontSize:13, color:'var(--nx-text-muted)', lineHeight:1.6, maxWidth:280 }}>
                  AI-powered insights for this record are being prepared. This feature will be available soon.
                </div>
              </div>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--nx-text-muted)', background:'var(--nx-surface-alt)', border:'1px solid var(--nx-divider)', borderRadius:20, padding:'4px 12px' }}>
                Coming Soon
              </span>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

const DrawerMore = ({ onDelete, record, type, extraItems, dangerItems }) => {
  const store = useStore();
  const [open, setOpen] = useStateC(false);
  const [reopenOpen, setReopenOpen] = useStateC(false);
  const ref = useRefC();
  useEffectC(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const toast = useToast();
  const isDeal = type === 'deals';
  const dealStage = isDeal ? (store.pipeline || []).find(s => s.id === record.stageId) : null;
  const isDealTerminal = isDeal && (dealStage?.kind === 'WON' || dealStage?.kind === 'LOST');

  const handleReopen = () => {
    setOpen(false);
    const ReopenModal = window.DealReopenModal;
    if (ReopenModal) { setReopenOpen(true); return; }
    toast({ tone: 'info', icon: 'RotateCcw', title: 'Reopen not available', desc: 'Move the deal to an open stage to reopen.' });
  };

  const handleExport = async () => {
    setOpen(false);
    try {
      if (type === 'people' && window._prepareCfExport && window._toCSV && window._download) {
        // Use canonical column definitions + CF values for accurate person export
        const { cols, hadCfError } = await window._prepareCfExport('people', [record], store);
        const fmtCols = cols.map(c => ({ ...c, get: c.get || ((r) => r[c.key] ?? '') }));
        const csv = window._toCSV([record], fmtCols);
        window._download('person-' + record.id + '.csv', 'text/csv', csv);
        toast({ tone: hadCfError ? 'info' : 'success', icon: 'File', title: 'Record exported' });
      } else {
        if (window.exportCSV) {
          window.exportCSV(type + '-' + record.id, [record]);
        } else {
          const cols = (window.EXPORT_COLUMNS?.[type] || Object.keys(record).filter(k => typeof record[k] !== 'object').map(k => ({ key: k, label: k })));
          const csv = cols.map(c => c.label).join(',') + '\n' + cols.map(c => { const v = record[c.key]; return v == null ? '' : ('"' + String(v).replace(/"/g, '""') + '"'); }).join(',');
          const a = document.createElement('a');
          a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
          a.download = type + '-' + record.id + '.csv';
          a.click();
          setTimeout(() => URL.revokeObjectURL(a.href), 1000);
        }
        toast({ tone: 'success', icon: 'File', title: 'Record exported' });
      }
    } catch (_) {
      toast({ tone: 'error', icon: 'AlertTriangle', title: 'Export failed' });
    }
  };

  return (
    <span ref={ref} style={{ position: 'relative' }}>
      <IconBtn icon="MoreHorizontal" className="nx-more-btn" aria-label="More options" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(o => !o)} />
      {open && (
        <div className="nx-chip-menu" role="menu" style={{ right: 0, left: 'auto', top: 30 }}>
          {extraItems && extraItems({ close: () => setOpen(false) })}
          {type !== 'tasks' && extraItems && <div className="nx-menu-section-label">Utilities</div>}
          <button type="button" role="menuitem" className="nx-chip-menu-item" onClick={handleExport}><NexusIcon name="File" size={11} /><span>Export Record</span></button>
          {isDealTerminal && (
            <button type="button" role="menuitem" className="nx-chip-menu-item" onClick={handleReopen}><NexusIcon name="RotateCcw" size={11} /><span>Reopen Deal</span></button>
          )}
          {type !== 'tasks' && window.ArchiveRestoreBtn &&
            React.createElement(window.ArchiveRestoreBtn, { record, type, onDone: () => setOpen(false) })
          }
          <div style={{ height: 1, background: 'var(--nx-divider)', margin: '4px 0' }} role="separator" />
          {dangerItems && dangerItems({ close: () => setOpen(false) })}
          <button type="button" role="menuitem" className="nx-chip-menu-item is-danger" onClick={() => { setOpen(false); onDelete(); }}><NexusIcon name="Trash" size={11} /><span>Delete</span></button>
        </div>
      )}
      {reopenOpen && window.DealReopenModal &&
        React.createElement(window.DealReopenModal, { record, onClose: () => setReopenOpen(false), onReopened: () => { setReopenOpen(false); store.closeDrawer(); } })
      }
    </span>
  );
};

/* ----- Overview ----- */
const OverviewTab = ({ record, type, isPerson, isCompany, isDeal, setTab }) => {
  const store = useStore();
  const _stages = store.pipeline || [];
  const stage = isDeal ? _stages.find(s => s.id === record.stageId) : null;

  const recordActivities = (store.activity || []).filter(a =>
    isPerson  ? a.personId  === record.id :
    isCompany ? a.companyId === record.id :
    isDeal    ? a.dealId    === record.id :
    false
  );

  const update = (field, value) => store.update(type, record.id, { [field]: value });

  return (
    <div className="nx-drawer-overview">
      <div className="nx-drawer-cols">
        <div className="nx-drawer-main">
          <DrawerPanel title="Properties" actions={<Btn variant="ghost" size="sm" icon="Edit" onClick={() => store.openEditForm(type, record)}>Edit All</Btn>}>
            <div className="nx-prop-grid">
              {isPerson && (
                <>
                  <EditableProp label="Email" value={record.email} mono onSave={v => update('email', v)} />
                  <EditableProp label="Phone" value={record.phone} mono onSave={v => update('phone', v)} />
                  <EditableProp label="Role" value={record.role} onSave={v => update('role', v)} />
                  <EditableProp label="Company" value={record.company} link onSave={v => update('company', v)} />
                  <EditableProp label="City" value={record.city} onSave={v => update('city', v)} />
                  <SelectProp label="Owner" value={record.owner} options={[window.__nx_auth?.user?.name].filter(Boolean)} onSave={v => update('owner', v)} avatar />
                  <SelectProp label="Status" value={record.status} options={['New','Engaged','Qualified','Customer']} onSave={v => update('status', v)} pill="blue" />
                  <TagEditor record={record} type="people" />
                </>
              )}
              {isCompany && (
                <>
                  <EditableProp label="Domain" value={record.domain} mono onSave={v => update('domain', v)} />
                  <SelectProp label="Industry" value={record.industry} options={['Software','Fintech','BFSI','Commerce','Media','Design Tools','CRM']} onSave={v => update('industry', v)} />
                  <EditableProp label="Employees" value={record.employees} mono numeric onSave={v => update('employees', Number(v) || 0)} />
                  <EditableProp label="Revenue" value={record.revenue} mono onSave={v => update('revenue', v)} />
                  <EditableProp label="Location" value={record.location} onSave={v => update('location', v)} />
                  <SelectProp label="Lifecycle" value={record.lifecycle} options={['Lead','Account','Enterprise']} onSave={v => update('lifecycle', v)} pill="purple" />
                  <SelectProp label="Owner" value={record.owner} options={[window.__nx_auth?.user?.name].filter(Boolean)} onSave={v => update('owner', v)} avatar />
                  <Prop label="Open Deals" value={record.deals} mono />
                </>
              )}
              {isDeal && (
                <>
                  <EditableProp label="Value" value={record.value} mono prefix="$" numeric onSave={v => update('value', Number(v) || 0)} />
                  <SelectProp label="Stage" value={record.stageId} options={_stages.map(s => ({ value: s.id, label: s.name }))} onSave={v => update('stageId', v)} pill="amber" />
                  <SelectProp label="Priority" value={record.priority} options={['High','Medium','Low']} onSave={v => update('priority', v)} pill={record.priority === 'High' ? 'red' : 'amber'} />
                  <EditableProp label="Close Date" value={record.closeDate} mono onSave={v => update('closeDate', v)} />
                  <Prop label="Age" value={record.age + ' days'} mono />
                  <SelectProp label="Owner" value={record.owner} options={[window.__nx_auth?.user?.name].filter(Boolean)} onSave={v => update('owner', v)} avatar />
                  <Prop label="Probability" value={<Score value={stage?.prob || 0} />} />
                </>
              )}
            </div>
            {isDeal && record.alerts && record.alerts.length > 0 && (
              <div className="nx-deal-alerts-row"><DealAlertChips alerts={record.alerts} /></div>
            )}
          </DrawerPanel>

          {isPerson && (store.modules || []).some(m => /bfsi/i.test(m)) && (
            <DrawerPanel title="BFSI Profile" actions={<Pill tone={record.riskLevel === 'Low' ? 'green' : record.riskLevel === 'High' ? 'red' : 'amber'}>{record.riskLevel || 'Risk —'}</Pill>}>
              <BFSIPersonProps record={record} />
            </DrawerPanel>
          )}
          {isDeal && (store.modules || []).some(m => /bfsi/i.test(m)) && (
            <DrawerPanel title="BFSI Loan Terms" actions={<Pill tone={record.repaymentStatus === 'On Time' ? 'green' : record.repaymentStatus === 'Overdue' ? 'red' : 'amber'}>{record.repaymentStatus}</Pill>}>
              <BFSIDealProps record={record} />
            </DrawerPanel>
          )}
          {isCompany && (
            <DrawerPanel title="Company Analytics">
              <CompanyAnalyticsPanel company={record} />
            </DrawerPanel>
          )}

          <DrawerPanel title="Notes" actions={<Btn variant="ghost" size="sm" icon="Plus" onClick={() => setTab?.('notes')}>Note</Btn>}>
            <NoteList record={record} limit={2} />
          </DrawerPanel>

          <DrawerPanel title="Recent Activity" actions={<button type="button" className="nx-panel-link" onClick={() => setTab?.('activity')}>See All<NexusIcon name="ArrowRight" size={11} /></button>}>
            {recordActivities.length === 0
              ? <span className="nx-muted nx-mini" style={{ padding: 8 }}>No activities yet.</span>
              : <Timeline items={recordActivities.slice(0, 4)} />}
          </DrawerPanel>
        </div>

        <div className="nx-drawer-side">
          <DrawerPanel title="Relationships">
            <RelationshipsPanel record={record} type={type} />
          </DrawerPanel>
          <DrawerPanel title="Open Tasks">
            <RelatedTasks record={record} isPerson={isPerson} />
          </DrawerPanel>
          {(isPerson || isCompany) && (
            <DrawerPanel title="Open Deals">
              <RelatedDeals record={record} isPerson={isPerson} />
            </DrawerPanel>
          )}
          <DrawerPanel title="Identifiers">
            <Prop label="Record ID" value={record.id} mono />
            <Prop label="Created" value={record.createdAt ? new Date(record.createdAt).toLocaleDateString('en-IN') : '—'} />
            <Prop label="Last Updated" value={record.updatedAt ? new Date(record.updatedAt).toLocaleDateString('en-IN') : '—'} />
          </DrawerPanel>
        </div>
      </div>
    </div>
  );
};

const RelatedTasks = ({ record, isPerson }) => {
  const store = useStore();
  const tasks = store.tasks.filter(t =>
    !t.done && (isPerson ? t.personId === record.id : t.companyId === record.id)
  ).slice(0, 3);
  const toggle = (id) => {
    const t = store.tasks.find(x => x.id === id);
    store.update('tasks', id, { done: !t.done });
  };
  const prefill = isPerson ? { personId: record.id } : { companyId: record.id };
  if (!tasks.length) return <span className="nx-muted nx-mini" style={{ padding: 8 }}>No open tasks.</span>;
  return (
    <>
      {tasks.map(t => (
        <div key={t.id} className="nx-mini-task">
          <button type="button" className="nx-check" onClick={() => toggle(t.id)} aria-label={t.done ? 'Reopen task' : 'Complete task'} />
          <span className="nx-task-title">{t.title}</span>
          {t.dueDate && <span className="nx-task-due">{t.dueDate}</span>}
        </div>
      ))}
      <Btn variant="ghost" size="sm" icon="Plus" className="nx-add-inline" onClick={() => store.openCreateForm('tasks', prefill)}>Add Task</Btn>
    </>
  );
};

const RelatedDeals = ({ record, isPerson }) => {
  const store = useStore();
  const deals = store.deals.filter(d => isPerson ? d.contactId === record.id : d.companyId === record.id).slice(0, 3);
  if (!deals.length) return <span className="nx-muted nx-mini" style={{ padding: 8 }}>No open deals.</span>;
  return (
    <>
      {deals.map(d => (
        <button type="button" key={d.id} className="nx-mini-deal" onClick={() => store.openDrawer(d)}>
          <span className="nx-mini-deal-title">{d.title}</span>
          <span className="nx-cell is-mono"><strong>${(d.value / 1000).toFixed(0)}K</strong></span>
        </button>
      ))}
    </>
  );
};

/* ----- Editable props (inline edit) ----- */
const EditableProp = ({ label, value, mono, link, numeric, prefix, date, onSave }) => {
  const [editing, setEditing] = useStateC(false);
  const [v, setV] = useStateC(value);
  useEffectC(() => setV(value), [value]);
  const finish = () => {
    const next = numeric ? v : (typeof v === 'string' ? v.trim() : v);
    setEditing(false);
    if (String(next) !== String(value)) onSave(next);
  };
  const raw = value == null || value === '' ? null : value;
  const display = raw == null
    ? '—'
    : date
      ? new Date(raw + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : prefix
        ? prefix + (typeof raw === 'number' ? raw.toLocaleString() : raw)
        : raw;
  return (
    <div className={'nx-prop is-editable' + (editing ? ' is-editing' : '')}>
      <span className="nx-prop-label">{label}</span>
      {editing ? (
        <input
          autoFocus
          className={'nx-prop-input' + (mono ? ' is-mono' : '')}
          type={date ? 'date' : numeric ? 'number' : 'text'}
          value={v ?? ''}
          onChange={e => setV(e.target.value)}
          onBlur={finish}
          onKeyDown={e => { if (e.key === 'Enter') finish(); if (e.key === 'Escape') { setV(value); setEditing(false); } }}
        />
      ) : (
        <button
          type="button"
          className={'nx-prop-value' + (mono ? ' is-mono' : '') + (link ? ' is-link' : '') + (raw == null ? ' is-placeholder' : '')}
          onClick={() => setEditing(true)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, textAlign: 'left', width: '100%' }}
        >
          <span>{display}</span>
          <NexusIcon name="Edit" size={11} className="nx-prop-edit" />
        </button>
      )}
    </div>
  );
};

const SelectProp = ({ label, value, options, onSave, avatar, pill }) => {
  const [open, setOpen] = useStateC(false);
  const ref = useRefC();
  useEffectC(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const opts = options.map(o => typeof o === 'object' ? o : { value: o, label: o });
  const current = opts.find(o => o.value === value);
  const display = current ? current.label : (value || '—');
  return (
    <div ref={ref} className="nx-prop is-editable" style={{ position: 'relative' }}>
      {label ? <span className="nx-prop-label">{label}</span> : null}
      <button
        type="button"
        className="nx-prop-value"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, textAlign: 'left', width: '100%' }}
      >
        {avatar && <Avatar name={display} tone="#125AB0" size={16} />}
        {pill ? <Pill tone={pill}>{display}</Pill> : <span>{display}</span>}
        <NexusIcon name="ChevronDown" size={11} className="nx-prop-edit" />
      </button>
      {open && (
        <div className="nx-chip-menu" role="listbox" style={{ top: 'calc(100% + 4px)' }}>
          {opts.map(o => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              className={'nx-chip-menu-item' + (o.value === value ? ' is-active' : '')}
              onClick={() => { onSave(o.value); setOpen(false); }}
            >
              <NexusIcon name="Check" size={11} style={{ opacity: o.value === value ? 1 : 0 }} />
              <span>{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DrawerPanel = ({ title, children, actions }) => (
  <div className="nx-drawer-panel">
    <div className="nx-drawer-panel-head">
      <h4 className="nx-drawer-panel-title">{title}</h4>
      {actions}
    </div>
    <div className="nx-drawer-panel-body">{children}</div>
  </div>
);

const Prop = ({ label, value, mono, link, avatar }) => (
  <div className="nx-prop">
    <span className="nx-prop-label">{label}</span>
    <span className={'nx-prop-value' + (mono ? ' is-mono' : '') + (link ? ' is-link' : '')}>
      {avatar && typeof value === 'string' && <Avatar name={value} tone="#125AB0" size={16} />}
      {typeof value === 'string' || typeof value === 'number' ? <span>{value}</span> : value}
    </span>
  </div>
);
window.DrawerPanel  = DrawerPanel;
window.Prop         = Prop;
window.EditableProp = EditableProp;
window.SelectProp   = SelectProp;

/* ----- Notes tab + list ----- */
const _fmtBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
};

const NoteList = ({ record, notes: notesProp, limit, editable, onDelete, onEdit, onRetryAttachment, onRemoveAttachment, emptyMsg }) => {
  const store = useStore();
  const [editingId, setEditingId] = useStateC(null);
  const [editBody,  setEditBody]  = useStateC('');

  const isPerson  = record?.role    !== undefined;
  const isCompany = record?.domain  !== undefined;
  const isDeal    = record?.stageId !== undefined;

  let displayNotes = notesProp !== undefined ? notesProp : store.notes.filter(n =>
    isPerson  ? n.personId  === record?.id :
    isCompany ? n.companyId === record?.id :
    isDeal    ? n.dealId    === record?.id :
    false
  );
  if (limit) displayNotes = displayNotes.slice(0, limit);

  if (!displayNotes.length) {
    return (
      <div style={{ padding: '12px 0', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)' }}>
        <NexusIcon name="StickyNote" size={14} />
        <span className="nx-mini">{emptyMsg || 'No notes yet.'}</span>
      </div>
    );
  }

  const startEdit  = (n) => { setEditingId(n.id); setEditBody(n.detail); };
  const cancelEdit = ()  => { setEditingId(null);  setEditBody(''); };
  const saveEdit   = (id) => {
    const v = editBody.trim();
    if (!v) return;
    if (onEdit) onEdit(id, v); else store.updateNote(id, { body: v });
    setEditingId(null);
    setEditBody('');
  };
  const handleDelete = (id) => {
    if (onDelete) onDelete(id); else store.removeNote(id);
  };

  return (
    <div className="nx-notes-list">
      {displayNotes.map(n => {
        const isEditing = editingId === n.id;
        return (
          <div key={n.id} className="nx-note">
            <div className="nx-note-head">
              <Avatar name={n.who} tone="#125AB0" size={20} />
              <span className="nx-note-author">{n.who}</span>
              <span className="nx-muted nx-mini">{n.time}</span>
              <span style={{ flex: 1 }} />
              {editable && !isEditing && (
                <span style={{ display: 'flex', gap: 4 }}>
                  <button type="button" className="nx-icon-btn" aria-label="Edit note" title="Edit note"
                    onClick={() => startEdit(n)}>
                    <NexusIcon name="Pencil" size={12} />
                  </button>
                  <button type="button" className="nx-icon-btn nx-icon-btn--danger" aria-label="Delete note" title="Delete note"
                    onClick={() => handleDelete(n.id)}>
                    <NexusIcon name="Trash2" size={12} />
                  </button>
                </span>
              )}
            </div>
            {n.noteTitle && <p style={{ fontWeight: 600, fontSize: 12, margin: '2px 0' }}>{n.noteTitle}</p>}
            {isEditing ? (
              <div>
                <textarea
                  className="nx-note-edit-area"
                  value={editBody}
                  autoFocus
                  onChange={e => setEditBody(e.target.value)}
                  onKeyDown={e => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') saveEdit(n.id);
                    if (e.key === 'Escape') cancelEdit();
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 4 }}>
                  <Btn variant="ghost" size="sm" onClick={cancelEdit}>Cancel</Btn>
                  <Btn variant="primary" size="sm" icon="Check"
                    disabled={!editBody.trim()}
                    onClick={() => saveEdit(n.id)}>Save</Btn>
                </div>
              </div>
            ) : (
              <>
                <p className="nx-note-body">{n.detail}</p>
                {Array.isArray(n.attachments) && n.attachments.length > 0 && (
                  <div className="nx-note-attachments">
                    {n.attachments.map((att, ai) => (
                      <div key={att.id || att.key || ai} className={`nx-note-attachment${att.status === 'failed' ? ' nx-note-attachment--failed' : ''}`}>
                        <NexusIcon name={att.status === 'failed' ? 'AlertCircle' : 'Paperclip'} size={11}
                          style={{ color: att.status === 'failed' ? 'var(--color-warning)' : 'var(--nx-text-muted)', flexShrink: 0 }} />
                        <span className="nx-attachment-name" title={att.fileName}>{att.fileName}</span>
                        {att.fileSize > 0 && <span className="nx-attachment-size">{_fmtBytes(att.fileSize)}</span>}
                        {att.status === 'failed' ? (
                          <>
                            <span className="nx-attachment-err">Upload failed</span>
                            {onRetryAttachment && (
                              <button type="button" className="nx-attachment-action" onClick={() => onRetryAttachment(n.id, att)}>Retry</button>
                            )}
                            {onRemoveAttachment && (
                              <button type="button" className="nx-attachment-action nx-attachment-action--remove" onClick={() => onRemoveAttachment(n.id, att.key)}>Remove</button>
                            )}
                          </>
                        ) : att.downloadUrl ? (
                          <button type="button" className="nx-attachment-action"
                            onClick={() => window.__nx_api?.document?.download?.(record?.workspaceId || window.__nx_store?.getState?.()?.workspaceId, att.id, att.fileName)}>
                            Download
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

const NotesTab = ({ record }) => {
  const store = useStore();
  const wsId  = store.workspaceId;
  const toast = useToast();
  const [draft,        setDraft]        = useStateC('');
  const [localItems,   setLocalItems]   = useStateC(null);
  const [fetchError,   setFetchError]   = useStateC(false);
  const [hasMore,      setHasMore]      = useStateC(false);
  const [page,         setPage]         = useStateC(1);
  const [loadingMore,  setLoadingMore]  = useStateC(false);
  const [retryKey,     setRetryKey]     = useStateC(0);
  const [pendingFiles, setPendingFiles] = useStateC([]);  /* { key, file }[] */
  const [saving,       setSaving]       = useStateC(false);
  const fileInputRef = useRefC(null);

  const isPerson  = record?.role    !== undefined;
  const isCompany = record?.domain  !== undefined;
  const isDeal    = record?.stageId !== undefined;

  useEffectC(() => {
    if (!wsId || !record?.id || (!isPerson && !isCompany && !isDeal)) return;
    const params = { pageSize: 25, page: 1 };
    if (isCompany) params.companyId = record.id;
    else if (isDeal) params.dealId  = record.id;
    else             params.personId = record.id;
    setLocalItems(null);
    setFetchError(false);
    setHasMore(false);
    setPage(1);
    window.__nx_api.note.list(wsId, params)
      .then(resp => {
        const raw = Array.isArray(resp) ? resp : (resp?.data ?? []);
        setLocalItems(raw.map(window.__nx_api.note.normNote));
        const p = resp?.pagination;
        setHasMore(!!p && 1 < p.totalPages);
      })
      .catch(() => setFetchError(true));
  }, [record?.id, wsId, retryKey]);

  const globalFiltered = store.notes.filter(n =>
    isPerson  ? n.personId  === record?.id :
    isCompany ? n.companyId === record?.id :
    isDeal    ? n.dealId    === record?.id :
    false
  );

  const displayNotes = localItems !== null
    ? [...globalFiltered.filter(g => !localItems.some(li => li.id === g.id)), ...localItems]
    : globalFiltered;

  const isLoading = localItems === null && !fetchError;

  const _entityParams = () => ({
    personId:  isPerson  ? record.id : undefined,
    companyId: isCompany ? record.id : undefined,
    dealId:    isDeal    ? record.id : undefined,
  });

  const save = async () => {
    const body = draft.trim();
    if (!body && pendingFiles.length === 0) return;
    if (saving) return;

    /* No attachments — use store's optimistic path */
    if (pendingFiles.length === 0) {
      store.createNote({ body, ..._entityParams() });
      setDraft('');
      return;
    }

    /* Has attachments — call API directly to get real noteId before uploading */
    setSaving(true);
    let createdNote;
    try {
      createdNote = await window.__nx_api.note.create(wsId, { body: body || '.', ..._entityParams() });
    } catch {
      setSaving(false);
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Note save failed', desc: 'Could not save note. Please try again.' });
      return;
    }

    const noteId = createdNote.id;

    /* Upload each pending file; track individual success/failure */
    const uploadResults = await Promise.all(
      pendingFiles.map(async ({ key, file }) => {
        try {
          const doc = await window.__nx_api.document.upload(wsId, file, { noteId });
          return { key, status: 'uploaded', doc };
        } catch {
          return { key, status: 'failed', file };
        }
      })
    );

    const normN = window.__nx_api.note.normNote(createdNote);
    const attachments = uploadResults.map(r =>
      r.status === 'uploaded'
        ? { id: r.doc.id, fileName: r.doc.fileName, mimeType: r.doc.mimeType, fileSize: r.doc.fileSize, downloadUrl: r.doc.downloadUrl, createdAt: r.doc.createdAt, status: 'uploaded' }
        : { key: r.key, fileName: r.file.name, fileSize: r.file.size, mimeType: r.file.type, status: 'failed', _file: r.file }
    );

    const noteEntry = { ...normN, attachments };
    setLocalItems(prev => [noteEntry, ...(prev || [])]);
    setDraft('');
    setPendingFiles([]);
    setSaving(false);

    const failed = uploadResults.filter(r => r.status === 'failed').length;
    if (failed > 0) {
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Note saved', desc: `${failed} attachment${failed > 1 ? 's' : ''} failed to upload. You can retry below.` });
    } else {
      toast({ tone: 'success', icon: 'FileText', title: 'Note saved' });
    }
  };

  const handleDelete = (noteId) => {
    store.removeNote(noteId);
    setLocalItems(prev => prev ? prev.filter(n => n.id !== noteId) : prev);
  };

  const handleEdit = (noteId, newBody) => {
    store.updateNote(noteId, { body: newBody });
    setLocalItems(prev => prev
      ? prev.map(n => n.id === noteId ? { ...n, detail: newBody } : n)
      : prev
    );
  };

  const retryUpload = async (noteId, failedAtt) => {
    if (!failedAtt._file) return;
    try {
      const doc = await window.__nx_api.document.upload(wsId, failedAtt._file, { noteId });
      setLocalItems(prev => prev ? prev.map(n => {
        if (n.id !== noteId) return n;
        return { ...n, attachments: n.attachments.map(a =>
          a.key === failedAtt.key
            ? { id: doc.id, fileName: doc.fileName, mimeType: doc.mimeType, fileSize: doc.fileSize, downloadUrl: doc.downloadUrl, createdAt: doc.createdAt, status: 'uploaded' }
            : a
        )};
      }) : prev);
    } catch { /* leave as failed */ }
  };

  const removeFailedAttachment = (noteId, attKey) => {
    setLocalItems(prev => prev ? prev.map(n => {
      if (n.id !== noteId) return n;
      return { ...n, attachments: n.attachments.filter(a => a.key !== attKey) };
    }) : prev);
  };

  const addFiles = (files) => {
    const items = Array.from(files).map(f => ({ key: Date.now() + Math.random(), file: f }));
    setPendingFiles(prev => [...prev, ...items]);
  };

  const removePendingFile = (key) => {
    setPendingFiles(prev => prev.filter(f => f.key !== key));
  };

  const loadMore = () => {
    if (loadingMore || !hasMore || !wsId) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    const params = { pageSize: 25, page: nextPage };
    if (isCompany) params.companyId = record.id;
    else if (isDeal) params.dealId  = record.id;
    else             params.personId = record.id;
    window.__nx_api.note.list(wsId, params)
      .then(resp => {
        const raw = Array.isArray(resp) ? resp : (resp?.data ?? []);
        setLocalItems(prev => [...(prev || []), ...raw.map(window.__nx_api.note.normNote)]);
        const p = resp?.pagination;
        setHasMore(!!p && nextPage < p.totalPages);
        setPage(nextPage);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const canSave = !saving && (draft.trim().length > 0 || pendingFiles.length > 0);

  return (
    <div className="nx-drawer-page">
      <div className="nx-note-composer">
        <Avatar name={window.__nx_auth?.user?.name || ''} tone="#125AB0" size={24} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Write a note… (Ctrl+Enter to save)"
            onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') save(); }}
          />
          {pendingFiles.length > 0 && (
            <div className="nx-composer-files">
              {pendingFiles.map(({ key, file }) => (
                <div key={key} className="nx-composer-file">
                  <NexusIcon name="Paperclip" size={11} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                  <span className="nx-attachment-name" title={file.name}>{file.name}</span>
                  <span className="nx-attachment-size">{_fmtBytes(file.size)}</span>
                  <button type="button" className="nx-icon-btn" style={{ marginLeft: 'auto' }} aria-label="Remove attachment" onClick={() => removePendingFile(key)}>
                    <NexusIcon name="X" size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, alignItems: 'center' }}>
            <button type="button" className="nx-icon-btn" title="Attach file" aria-label="Attach file"
              onClick={() => fileInputRef.current?.click()}>
              <NexusIcon name="Paperclip" size={14} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
            />
            <Btn variant="primary" size="sm" disabled={!canSave} onClick={save}>
              {saving ? 'Saving…' : 'Save Note'}
            </Btn>
          </div>
        </div>
      </div>

      {isLoading && globalFiltered.length === 0 ? (
        <div className="nx-skeleton-list">
          {[0, 1, 2].map(i => (
            <div key={i} className="nx-skeleton-row" style={{ height: 56, marginBottom: 8 }}>
              <div className="nx-skeleton-line" style={{ width: '40%' }} />
              <div className="nx-skeleton-line" style={{ width: '70%', marginTop: 4 }} />
            </div>
          ))}
        </div>
      ) : fetchError ? (
        <div className="nx-empty" style={{ flexDirection: 'column', gap: 8, padding: '16px 0' }}>
          <NexusIcon name="AlertCircle" size={20} style={{ color: 'var(--color-warning)' }} />
          <span className="nx-muted nx-mini">Could not load notes.</span>
          <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={() => setRetryKey(k => k + 1)}>Retry</Btn>
        </div>
      ) : (
        <>
          <NoteList
            record={record}
            notes={displayNotes}
            editable
            onDelete={handleDelete}
            onEdit={handleEdit}
            onRetryAttachment={retryUpload}
            onRemoveAttachment={removeFailedAttachment}
            emptyMsg="No notes yet — write one above."
          />
          {hasMore && (
            <Btn variant="ghost" size="sm"
              icon={loadingMore ? 'Loader' : 'ChevronDown'}
              style={{ margin: '4px 0', width: '100%' }}
              disabled={loadingMore}
              onClick={loadMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </Btn>
          )}
        </>
      )}
    </div>
  );
};

const ACT_ICON = {
  whatsapp: 'MessageCircle', deal: 'Target',       email: 'Mail',
  note:     'StickyNote',    call: 'Phone',         meeting: 'Calendar',
  followup: 'Clock',         task: 'CheckSquare',
};
const ACT_COLOR = {
  whatsapp: '#0E8C5A', deal: '#125AB0',  email: '#6E3FBA',
  note:     '#C26A1F', call: '#0E8C5A',  meeting: '#0E6FBF',
  followup: '#A8508A', task: '#64748b',
};

const ActivityTab = ({ record, intent, onConsumeIntent }) => {
  const store = useStore();
  const wsId  = store.workspaceId;
  const [open, setOpen]       = useStateC(false);
  const [type, setType]       = useStateC('call');
  const [detail, setDetail]   = useStateC('');
  /* Per-entity fetch state */
  const [localItems, setLocalItems] = useStateC(null);   // null = loading/not yet fetched
  const [fetchError, setFetchError] = useStateC(false);
  const [hasMore, setHasMore]       = useStateC(false);
  const [page, setPage]             = useStateC(1);
  const [loadingMore, setLoadingMore] = useStateC(false);
  const [retryKey, setRetryKey]     = useStateC(0);

  useEffectC(() => {
    if (intent?.type) { setType(intent.type); setOpen(true); onConsumeIntent?.(); }
  }, [intent]);

  const isPerson  = record?.role   !== undefined;
  const isCompany = record?.domain !== undefined;
  const isDeal    = record?.stageId !== undefined;

  /* Fetch the first page of activities for this entity */
  useEffectC(() => {
    if (!wsId || !record?.id) return;
    const params = { pageSize: 25, page: 1 };
    if (isCompany)     params.companyId = record.id;
    else if (isPerson) params.personId  = record.id;
    else if (isDeal)   params.dealId    = record.id;
    else               return; // unknown entity type — skip fetch
    setLocalItems(null);
    setFetchError(false);
    setHasMore(false);
    setPage(1);
    window.__nx_api.activity.list(wsId, params)
      .then(resp => {
        const raw = Array.isArray(resp) ? resp : (resp?.data ?? []);
        setLocalItems(raw.map(window.__nx_api.activity.normActivity));
        const p = resp?.pagination;
        setHasMore(!!p && 1 < p.totalPages);
      })
      .catch(() => setFetchError(true));
  }, [record?.id, wsId, retryKey]);

  const types = [
    { id: 'call',     label: 'Call',      icon: 'Phone' },
    { id: 'meeting',  label: 'Meeting',   icon: 'Calendar' },
    { id: 'email',    label: 'Email',     icon: 'Mail' },
    { id: 'whatsapp', label: 'WhatsApp',  icon: 'MessageCircle' },
    { id: 'followup', label: 'Follow-Up', icon: 'Clock' },
  ];

  const save = () => {
    if (!detail.trim()) return;
    const t = types.find(x => x.id === type);
    store.logActivity({
      type,
      who:       window.__nx_auth?.user?.name || '',
      what:      'logged ' + t.label.toLowerCase(),
      detail:    detail.trim(),
      personId:  isPerson  ? record.id : undefined,
      companyId: isCompany ? record.id : undefined,
      dealId:    isDeal    ? record.id : undefined,
    });
    setDetail(''); setOpen(false);
  };

  /* Merge: prepend optimistic entries from global store not yet in localItems */
  const globalFiltered = store.activity.filter(a =>
    isPerson  ? a.personId  === record?.id :
    isCompany ? a.companyId === record?.id :
    isDeal    ? a.dealId    === record?.id :
    false
  );
  const displayItems = localItems !== null
    ? [...globalFiltered.filter(g => !localItems.some(li => li.id === g.id)), ...localItems]
    : globalFiltered;

  const isLoading = localItems === null && !fetchError;

  return (
    <div className="nx-drawer-page">
      <div style={{ display: 'flex', marginBottom: 12 }}>
        <Btn variant="primary" icon="Plus" size="sm" onClick={() => setOpen(o => !o)}>Add Activity</Btn>
      </div>

      {open && (
        <div className="nx-activity-composer">
          <div className="nx-activity-types">
            {types.map(t => (
              <button type="button" key={t.id} className={'nx-activity-type' + (type === t.id ? ' is-active' : '')} onClick={() => setType(t.id)}>
                <NexusIcon name={t.icon} size={12} /><span>{t.label}</span>
              </button>
            ))}
          </div>
          <textarea className="nx-activity-detail" value={detail} onChange={e => setDetail(e.target.value)} placeholder="What happened? Add a note…" autoFocus />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <Btn variant="ghost" size="sm" onClick={() => { setOpen(false); setDetail(''); }}>Cancel</Btn>
            <Btn variant="primary" size="sm" icon="Check" onClick={save}>Log Activity</Btn>
          </div>
        </div>
      )}

      {/* Loading skeleton (only when cache is also empty) */}
      {isLoading && globalFiltered.length === 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--nx-bg-muted)', flexShrink: 0 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ height: 12, width: '60%', borderRadius: 4, background: 'var(--nx-bg-muted)' }} />
                <div style={{ height: 10, width: '38%', borderRadius: 4, background: 'var(--nx-bg-muted)', opacity: 0.6 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error state */}
      {fetchError && (
        <div className="nx-empty" style={{ padding: 'var(--space-8) 0', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <NexusIcon name="AlertCircle" size={20} />
          <span>Could not load activities.</span>
          <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={() => { setFetchError(false); setRetryKey(k => k + 1); }}>Retry</Btn>
        </div>
      )}

      {/* Empty state */}
      {!fetchError && !isLoading && displayItems.length === 0 && !open && (
        <div className="nx-empty" style={{ padding: 'var(--space-10) 0', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <NexusIcon name="Activity" size={24} />
          <span>No activities yet — log the first one above.</span>
        </div>
      )}

      {/* Timeline */}
      {!fetchError && displayItems.length > 0 && <Timeline items={displayItems} />}

      {/* Load More */}
      {!fetchError && hasMore && (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12 }}>
          <Btn
            variant="ghost" size="sm"
            icon={loadingMore ? 'Loader' : 'ChevronDown'}
            disabled={loadingMore}
            onClick={() => {
              if (loadingMore) return;
              const nextPage = page + 1;
              setLoadingMore(true);
              const params = { pageSize: 25, page: nextPage };
              if (isCompany)     params.companyId = record.id;
              else if (isPerson) params.personId  = record.id;
              else if (isDeal)   params.dealId    = record.id;
              window.__nx_api.activity.list(wsId, params)
                .then(resp => {
                  const raw = Array.isArray(resp) ? resp : (resp?.data ?? []);
                  const more = raw.map(window.__nx_api.activity.normActivity);
                  setLocalItems(prev => [...(prev || []), ...more]);
                  const p = resp?.pagination;
                  setHasMore(!!p && nextPage < p.totalPages);
                  setPage(nextPage);
                })
                .catch(() => {})
                .finally(() => setLoadingMore(false));
            }}
          >
            {loadingMore ? 'Loading…' : 'Load More'}
          </Btn>
        </div>
      )}
    </div>
  );
};

const Timeline = ({ items }) => (
  <ol className="nx-timeline">
    {items.map(a => (
      <li key={a.id} className="nx-timeline-item">
        <span className="nx-timeline-dot" style={{ background: ACT_COLOR[a.type] || '#94a3b8' }}>
          <NexusIcon name={ACT_ICON[a.type] || 'Activity'} size={11} />
        </span>
        <div className="nx-timeline-body">
          <span className="nx-timeline-text"><strong>{a.who}</strong> {a.what}</span>
          {a.detail && <span className="nx-timeline-detail">{a.detail}</span>}
          <span className="nx-timeline-time">{a.time}</span>
        </div>
      </li>
    ))}
  </ol>
);
window.Timeline = Timeline;

const TASK_PRI_TONE = { High: 'red', Urgent: 'red', Medium: 'amber', Low: 'gray' };

const DrawerTasksTab = ({ record }) => {
  const store = useStore();
  const toast = useToast();
  const wsId  = store.workspaceId;
  const [localItems,  setLocalItems]  = useStateC(null);
  const [fetchError,  setFetchError]  = useStateC(false);
  const [hasMore,     setHasMore]     = useStateC(false);
  const [page,        setPage]        = useStateC(1);
  const [loadingMore, setLoadingMore] = useStateC(false);
  const [retryKey,    setRetryKey]    = useStateC(0);
  const [filter,      setFilter]      = useStateC('open');

  const isPerson  = record?.role    !== undefined;
  const isCompany = record?.domain  !== undefined;
  const isDeal    = record?.stageId !== undefined;

  const prefill = isPerson  ? { personId: record.id, companyId: record.companyId || null, assignee: record.owner || '', _context: { source: 'person', readOnly: ['personId', 'companyId', 'assignee'], personId: record.id } }
                : isCompany ? { companyId: record.id }
                :              { dealId:    record.id };

  useEffectC(() => {
    if (!wsId || !record?.id) return;
    const params = { pageSize: 25, page: 1 };
    if (isPerson)       params.personId  = record.id;
    else if (isCompany) params.companyId = record.id;
    else if (isDeal)    params.dealId    = record.id;
    else                return;
    setLocalItems(null);
    setFetchError(false);
    setHasMore(false);
    setPage(1);
    window.__nx_api.task.list(wsId, params)
      .then(resp => {
        const raw = Array.isArray(resp) ? resp : (resp?.data ?? []);
        setLocalItems(raw.map(window.__nx_api.task.normTask));
        const p = resp?.pagination;
        setHasMore(!!p && 1 < p.totalPages);
      })
      .catch(() => setFetchError(true));
  }, [record?.id, wsId, retryKey]);

  const globalFiltered = store.tasks.filter(t =>
    isPerson  ? t.personId  === record?.id :
    isCompany ? t.companyId === record?.id :
    isDeal    ? t.dealId    === record?.id :
    false
  );
  /* globalFiltered is authoritative — edits via openEditForm reflect immediately.
     localOnly adds paginated items beyond the global 100-item workspace cache. */
  const seen = new Set(globalFiltered.map(g => g.id));
  const localOnly = (localItems || []).filter(li => !seen.has(li.id));
  const allTasks = localItems !== null
    ? [...globalFiltered, ...localOnly]
    : globalFiltered;

  const displayTasks = filter === 'open' ? allTasks.filter(t => !t.done && t.status !== 'cancelled')
                     : filter === 'done' ? allTasks.filter(t => t.done)
                     :                     allTasks;

  const openCount = allTasks.filter(t => !t.done && t.status !== 'cancelled').length;
  const isLoading = localItems === null && !fetchError;

  const toggle = (id) => {
    const t = allTasks.find(x => x.id === id);
    if (!t) return;
    if (t.status === 'cancelled') { store.update('tasks', id, { done: false }); return; }
    store.update('tasks', id, { done: !t.done });
  };

  const handleDelete = (id) => {
    store.remove('tasks', id);
    /* Mirror to localItems so deleted task leaves localOnly and doesn't reappear */
    setLocalItems(prev => prev ? prev.filter(t => t.id !== id) : prev);
  };

  const loadMore = () => {
    if (loadingMore || !hasMore || !wsId) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    const params = { pageSize: 25, page: nextPage };
    if (isPerson)       params.personId  = record.id;
    else if (isCompany) params.companyId = record.id;
    else                params.dealId    = record.id;
    window.__nx_api.task.list(wsId, params)
      .then(resp => {
        const raw = Array.isArray(resp) ? resp : (resp?.data ?? []);
        setLocalItems(prev => [...(prev || []), ...raw.map(window.__nx_api.task.normTask)]);
        const p = resp?.pagination;
        setHasMore(!!p && nextPage < p.totalPages);
        setPage(nextPage);
      })
      .catch(() => { toast({ tone: 'critical', icon: 'AlertCircle', title: 'Could not load more tasks.' }); })
      .finally(() => setLoadingMore(false));
  };

  return (
    <div className="nx-drawer-page">
      <div className="nx-tasks-header">
        <span className="nx-tasks-count">
          {isLoading ? '…' : openCount} open
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          {['all', 'open', 'done'].map(f => (
            <button key={f} type="button"
              className={'nx-act-filter-btn' + (filter === f ? ' is-active' : '')}
              onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f === 'open' ? 'Open' : 'Done'}
            </button>
          ))}
        </div>
        <Btn variant="ghost" size="sm" icon="Plus" onClick={() => store.openCreateForm('tasks', prefill)}>
          Add Task
        </Btn>
      </div>

      {isLoading && globalFiltered.length === 0 ? (
        <div className="nx-skeleton-list">
          {[0, 1, 2].map(i => (
            <div key={i} className="nx-skeleton-row" style={{ height: 52, marginBottom: 8 }}>
              <div className="nx-skeleton-line" style={{ width: '60%' }} />
              <div className="nx-skeleton-line" style={{ width: '35%', marginTop: 4 }} />
            </div>
          ))}
        </div>
      ) : fetchError ? (
        <div className="nx-empty" style={{ flexDirection: 'column', gap: 8, padding: '16px 0' }}>
          <NexusIcon name="AlertCircle" size={20} style={{ color: 'var(--color-warning)' }} />
          <span className="nx-muted nx-mini">Could not load tasks.</span>
          <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={() => setRetryKey(k => k + 1)}>Retry</Btn>
        </div>
      ) : displayTasks.length === 0 ? (
        <div className="nx-empty" style={{ padding: 'var(--space-10) 0', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <NexusIcon name="CheckSquare" size={24} />
          <span>{filter === 'done' ? 'No completed tasks.' : 'No tasks for ' + (record?.name || 'this record') + ' yet.'}</span>
          {filter !== 'done' && (
            <Btn variant="secondary" size="sm" icon="Plus" onClick={() => store.openCreateForm('tasks', prefill)}>
              Create First Task
            </Btn>
          )}
        </div>
      ) : (
        <>
          <div className="nx-task-list-crm">
            {displayTasks.map(t => (
              <div key={t.id} className={'nx-task-crm-row' + (t.done ? ' is-done' : '')}>
                <button type="button" className="nx-check"
                  aria-label={(t.done || t.status === 'cancelled') ? 'Reopen task' : 'Complete task'}
                  title={(t.done || t.status === 'cancelled') ? 'Reopen' : 'Complete'}
                  onClick={e => { e.stopPropagation(); toggle(t.id); }}>
                  {t.done && <NexusIcon name="Check" size={10} />}
                </button>
                <div className="nx-task-crm-body">
                  <span className="nx-task-crm-title">{t.title}</span>
                  <div className="nx-task-crm-meta">
                    {t.dueDate  && <span className="nx-task-meta-chip"><NexusIcon name="Calendar" size={9} />{t.dueDate}</span>}
                    {t.assignee && <span className="nx-task-meta-chip"><NexusIcon name="User" size={9} />{t.assignee.split(' ')[0]}</span>}
                    {t.status === 'cancelled'   && <span className="nx-task-meta-chip" style={{ color: 'var(--text-danger)' }}><NexusIcon name="XCircle" size={9} />Cancelled</span>}
                    {t.status === 'in_progress' && <span className="nx-task-meta-chip" style={{ color: 'var(--color-primary)' }}><NexusIcon name="Loader" size={9} />In Progress</span>}
                  </div>
                </div>
                <Pill tone={TASK_PRI_TONE[t.priority] || 'gray'}>{t.priority}</Pill>
                <span style={{ display: 'flex', gap: 3, marginLeft: 4 }}>
                  <button type="button" className="nx-icon-btn" aria-label="Edit task" title="Edit task"
                    onClick={() => store.openEditForm('tasks', t)}>
                    <NexusIcon name="Pencil" size={12} />
                  </button>
                  <button type="button" className="nx-icon-btn nx-icon-btn--danger" aria-label="Delete task" title="Delete task"
                    onClick={() => handleDelete(t.id)}>
                    <NexusIcon name="Trash2" size={12} />
                  </button>
                </span>
              </div>
            ))}
          </div>
          {hasMore && (
            <Btn variant="ghost" size="sm"
              icon={loadingMore ? 'Loader' : 'ChevronDown'}
              style={{ margin: '4px 0', width: '100%' }}
              disabled={loadingMore}
              onClick={loadMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </Btn>
          )}
        </>
      )}
    </div>
  );
};

const EmailsTab = ({ record, autoCompose, onConsumeCompose }) => {
  const store  = useStore();
  const wsId   = store.workspaceId;
  const toast  = useToast();
  const name   = record?.name || record?.title || '';

  const [composing,   setComposing]   = useStateC(false);
  const [to,          setTo]          = useStateC('');
  const [cc,          setCc]          = useStateC('');
  const [subject,     setSubject]     = useStateC('');
  const [body,        setBody]        = useStateC('');
  const [sending,     setSending]     = useStateC(false);
  const [savingDraft, setSavingDraft] = useStateC(false);
  const [templates,   setTemplates]   = useStateC([]);
  const [localItems,  setLocalItems]  = useStateC(null);
  const [fetchError,  setFetchError]  = useStateC(false);
  const [retryKey,    setRetryKey]    = useStateC(0);
  const [filter,      setFilter]      = useStateC('all');
  const [replyTo,     setReplyTo]     = useStateC(null);
  const [expandedId,  setExpandedId]  = useStateC(null);

  const isPerson  = record?.role    !== undefined;
  const isCompany = record?.domain  !== undefined;
  const isDeal    = record?.stageId !== undefined;

  useEffectC(() => {
    if (autoCompose) { setComposing(true); onConsumeCompose?.(); }
  }, [autoCompose]);

  useEffectC(() => { setExpandedId(null); }, [record?.id]);

  useEffectC(() => {
    if (!wsId || !record?.id) return;
    const params = {};
    if (isPerson)       params.personId  = record.id;
    else if (isCompany) params.companyId = record.id;
    else if (isDeal)    params.dealId    = record.id;
    else                return;
    setLocalItems(null);
    setFetchError(false);
    window.__nx_api.email.listEmails(wsId, params)
      .then(resp => {
        const raw = Array.isArray(resp) ? resp : (resp?.data ?? []);
        setLocalItems(raw.map(window.__nx_api.email.normEmail));
      })
      .catch(() => setFetchError(true));
  }, [record?.id, wsId, retryKey]);

  useEffectC(() => {
    if (!composing || !wsId) return;
    window.__nx_api.email.listTemplates(wsId)
      .then(resp => {
        const raw = Array.isArray(resp) ? resp : [];
        if (raw.length) setTemplates(raw.map(t => ({ label: t.name, subject: t.subject || '', body: t.body || '' })));
      })
      .catch(() => {});
  }, [composing, wsId]);

  const hardcodedTemplates = useMemoC(() => [
    { label: 'Follow-Up',      subject: 'Following up on our conversation',     body: 'Hi ' + name.split(' ')[0] + ',\n\nJust circling back on our last conversation — happy to answer any questions and share next steps when you have a moment.\n\nBest,\n' + (window.__nx_auth?.user?.name || '') },
    { label: 'Send Documents', subject: 'Documents shared for your review',      body: 'Hi ' + name.split(' ')[0] + ',\n\nPlease find the attached documents for your review. Let me know if anything needs clarification.\n\nBest,\n' + (window.__nx_auth?.user?.name || '') },
    { label: 'Schedule Call',  subject: 'Quick call this week?',                 body: 'Hi ' + name.split(' ')[0] + ',\n\nWould you have 20 minutes this week for a quick call to align on next steps?\n\nBest,\n' + (window.__nx_auth?.user?.name || '') },
  ], [name]);

  const displayTemplates = templates.length ? templates : hardcodedTemplates;

  const resetCompose = () => {
    setTo(''); setCc(''); setSubject(''); setBody('');
    setComposing(false); setReplyTo(null);
  };

  const openReply = (email) => {
    setTo(email.fromEmail || '');
    setCc('');
    setSubject('Re: ' + (email.subject || ''));
    setBody('\n\n— On ' + email.time + ', ' + email.fromName + ' wrote:\n' + (email.body || ''));
    setReplyTo(email);
    setComposing(true);
    setExpandedId(null);
  };

  const openForward = (email) => {
    setTo('');
    setCc('');
    setSubject('Fwd: ' + (email.subject || ''));
    setBody('\n\n---------- Forwarded Message ----------\nFrom: ' + email.fromName + '\nSubject: ' + (email.subject || '') + '\n\n' + (email.body || ''));
    setReplyTo(null);
    setComposing(true);
    setExpandedId(null);
  };

  const _buildPayload = (status) => {
    const user = window.__nx_auth?.user;
    return {
      subject:   subject.trim() || undefined,
      body:      body.trim()    || undefined,
      fromName:  user?.name     || '',
      fromEmail: user?.email    || '',
      toEmails:  to.trim() ? [to.trim()] : [],
      ccEmails:  cc.trim() ? cc.split(',').map(s => s.trim()).filter(Boolean) : [],
      type:      status === 'DRAFT' ? 'DRAFT' : 'OUTGOING',
      status,
      threadId:  replyTo ? (replyTo.threadId || replyTo.id) : undefined,
      personId:  isPerson  ? record.id : undefined,
      companyId: isCompany ? record.id : undefined,
      dealId:    isDeal    ? record.id : undefined,
    };
  };

  const send = () => {
    if (!body.trim() && !subject.trim()) return;
    if (sending || savingDraft) return;
    if (!to.trim()) {
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Recipient required', desc: 'Please enter a To address before sending.' });
      return;
    }
    setSending(true);
    const user = window.__nx_auth?.user;
    if (wsId) {
      window.__nx_api.email.createEmail(wsId, _buildPayload('SENT'))
        .then(e => {
          const normed = window.__nx_api.email.normEmail(e);
          setLocalItems(prev => [normed, ...(prev || [])]);
          store.logActivity({
            type:      'email',
            who:       user?.name  || '',
            what:      replyTo ? 'replied to email from ' + replyTo.fromName : 'sent email to ' + name,
            detail:    subject.trim() || body.trim().slice(0, 80),
            personId:  isPerson  ? record.id : undefined,
            companyId: isCompany ? record.id : undefined,
            dealId:    isDeal    ? record.id : undefined,
          });
          toast({ tone: 'success', icon: 'Mail', title: replyTo ? 'Reply logged' : 'Email logged', desc: 'To: ' + to.trim() });
          resetCompose();
        })
        .catch(() => toast({ tone: 'warning', icon: 'AlertCircle', title: 'Email log failed', desc: 'Could not save. Please try again.' }))
        .finally(() => setSending(false));
    } else {
      setSending(false);
    }
  };

  const saveDraft = () => {
    if (savingDraft || sending) return;
    setSavingDraft(true);
    if (wsId) {
      window.__nx_api.email.createEmail(wsId, _buildPayload('DRAFT'))
        .then(e => {
          const normed = window.__nx_api.email.normEmail(e);
          setLocalItems(prev => [normed, ...(prev || [])]);
          toast({ tone: 'success', icon: 'FileText', title: 'Draft saved' });
          resetCompose();
        })
        .catch(() => toast({ tone: 'warning', icon: 'AlertCircle', title: 'Draft save failed', desc: 'Could not save. Please try again.' }))
        .finally(() => setSavingDraft(false));
    } else {
      setSavingDraft(false);
    }
  };

  const handleDelete = (emailId) => {
    if (!wsId) return;
    setLocalItems(prev => prev ? prev.filter(e => e.id !== emailId) : prev);
    window.__nx_api.email.removeEmail(wsId, emailId)
      .then(() => toast({ tone: 'success', icon: 'Trash', title: 'Email deleted' }))
      .catch(() => {
        setRetryKey(k => k + 1);
        toast({ tone: 'warning', icon: 'AlertCircle', title: 'Delete failed', desc: 'Email restored.' });
      });
  };

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);

  const isLoading   = localItems === null && !fetchError;
  const allEmails   = localItems || [];
  const draftCount  = allEmails.filter(e => e.type === 'draft').length;
  const displayEmails = filter === 'sent'     ? allEmails.filter(e => e.type === 'sent')
                      : filter === 'received' ? allEmails.filter(e => e.type === 'received')
                      : filter === 'drafts'   ? allEmails.filter(e => e.type === 'draft')
                      :                         allEmails;

  const canSend      = to.trim() && (body.trim() || subject.trim());
  const canSaveDraft = body.trim() || subject.trim() || to.trim();

  const FILTERS = [
    { key: 'all',      label: 'All' },
    { key: 'sent',     label: 'Sent' },
    { key: 'received', label: 'Received' },
    { key: 'drafts',   label: draftCount > 0 ? 'Drafts (' + draftCount + ')' : 'Drafts' },
  ];

  return (
    <div className="nx-drawer-page">
      {/* Header: count · filters · compose */}
      <div className="nx-email-tab-head">
        <span className="nx-email-tab-count">
          {isLoading ? '…' : allEmails.length} {allEmails.length === 1 ? 'email' : 'emails'}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          {FILTERS.map(f => (
            <button key={f.key} type="button"
              className={'nx-act-filter-btn' + (filter === f.key ? ' is-active' : '')}
              onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        <Btn
          variant={composing ? 'ghost' : 'primary'}
          size="sm"
          icon="Mail"
          active={composing}
          onClick={() => { if (!composing) setComposing(true); }}>
          Compose
        </Btn>
      </div>

      {/* Compose / Reply panel */}
      {composing && (
        <div className="nx-email-compose-panel">
          <div className="nx-email-compose-head">
            <NexusIcon name={replyTo ? 'CornerUpLeft' : 'Mail'} size={13} style={{ color: 'var(--nx-accent)' }} />
            <span className="nx-email-compose-title">{replyTo ? 'Reply' : 'New Email'}</span>
            <button type="button" className="nx-icon-btn" aria-label="Close compose" title="Close"
              onClick={resetCompose}>
              <NexusIcon name="X" size={12} />
            </button>
          </div>

          {!replyTo && (
            <div className="nx-email-tpl-scroller">
              <span className="nx-prop-label" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>Templates</span>
              {displayTemplates.map(t => (
                <button type="button" key={t.label} className="nx-email-tpl"
                  onClick={() => { setSubject(t.subject); setBody(t.body); }}>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          <div className="nx-email-fields">
            <div className="nx-email-field">
              <span className="nx-email-field-label">To</span>
              <input className="nx-email-field-input" value={to}
                onChange={e => setTo(e.target.value)} placeholder="email@example.com" autoComplete="email" />
            </div>
            <div className="nx-email-field">
              <span className="nx-email-field-label">CC</span>
              <input className="nx-email-field-input" value={cc}
                onChange={e => setCc(e.target.value)} placeholder="Optional, comma-separated" />
            </div>
            <div className="nx-email-field">
              <span className="nx-email-field-label">Subject</span>
              <input className="nx-email-field-input" value={subject}
                onChange={e => setSubject(e.target.value)} placeholder="Add a subject" />
            </div>
          </div>

          <textarea className="nx-email-compose-body" value={body}
            onChange={e => setBody(e.target.value)} placeholder="Write your message…" rows={5} />

          <div className="nx-email-compose-footer">
            <Btn variant="ghost" size="sm" onClick={resetCompose}>Cancel</Btn>
            <Btn variant="ghost" size="sm"
              icon={savingDraft ? 'Loader' : 'FileText'}
              disabled={savingDraft || sending || !canSaveDraft}
              onClick={saveDraft}>
              {savingDraft ? 'Saving…' : 'Save Draft'}
            </Btn>
            <Btn variant="primary" size="sm"
              icon={sending ? 'Loader' : (replyTo ? 'CornerUpLeft' : 'Send')}
              disabled={sending || savingDraft || !canSend}
              onClick={send}>
              {sending ? 'Sending…' : (replyTo ? 'Reply' : 'Log Email')}
            </Btn>
          </div>
        </div>
      )}

      {/* Email list states */}
      {isLoading ? (
        <div className="nx-skeleton-list">
          {[0, 1, 2].map(i => (
            <div key={i} className="nx-skeleton-row" style={{ height: 68, marginBottom: 4 }}>
              <div className="nx-skeleton-line" style={{ width: '45%' }} />
              <div className="nx-skeleton-line" style={{ width: '70%', marginTop: 5 }} />
              <div className="nx-skeleton-line" style={{ width: '55%', marginTop: 5 }} />
            </div>
          ))}
        </div>
      ) : fetchError ? (
        <div className="nx-empty-state">
          <div className="nx-empty-state-icon"><NexusIcon name="AlertCircle" size={18} /></div>
          <span className="nx-empty-state-title">Could not load emails</span>
          <span className="nx-empty-state-desc">There was a problem fetching email history.</span>
          <Btn variant="secondary" size="sm" icon="RefreshCw"
            onClick={() => setRetryKey(k => k + 1)}>Retry</Btn>
        </div>
      ) : displayEmails.length === 0 ? (
        <div className="nx-empty-state">
          <div className="nx-empty-state-icon">
            <NexusIcon name={filter === 'drafts' ? 'FileText' : 'Mail'} size={18} />
          </div>
          <span className="nx-empty-state-title">
            {filter === 'drafts' ? 'No drafts' : filter !== 'all' ? 'No ' + filter + ' emails' : 'No emails yet'}
          </span>
          <span className="nx-empty-state-desc">
            {filter === 'drafts'
              ? 'Save a draft while composing to continue it here.'
              : filter !== 'all'
              ? 'Switch to All to see all emails for this record.'
              : 'Log your first email with ' + (record?.name || 'this record') + ' to start a conversation history.'}
          </span>
          {filter === 'all' && !composing && (
            <Btn variant="secondary" size="sm" icon="Mail" onClick={() => setComposing(true)}>
              Compose Email
            </Btn>
          )}
        </div>
      ) : (
        <div className="nx-email-card-list">
          {displayEmails.map(e => {
            const isExpanded = expandedId === e.id;
            const isDraft    = e.type === 'draft';
            return (
              <div key={e.id} className={'nx-email-card' + (isExpanded ? ' is-expanded' : '')}>
                <div className="nx-email-card-top" onClick={() => toggleExpand(e.id)} style={{ cursor: 'pointer' }}>
                  <Avatar name={e.fromName} tone="#6E3FBA" size={20} />
                  <span className="nx-email-card-name">{e.fromName}</span>
                  {isDraft ? (
                    <span className="nx-email-dir is-draft">
                      <NexusIcon name="FileText" size={10} />
                      Draft
                    </span>
                  ) : (
                    <span className={'nx-email-dir ' + (e.type === 'received' ? 'is-received' : 'is-sent')}>
                      <NexusIcon name={e.type === 'received' ? 'ArrowDownLeft' : 'ArrowUpRight'} size={10} />
                      {e.type === 'received' ? 'Received' : 'Sent'}
                    </span>
                  )}
                  <span className="nx-email-card-time">{e.time}</span>
                  <NexusIcon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={12}
                    style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
                  <span className="nx-email-card-actions" onClick={ev => ev.stopPropagation()}>
                    <button type="button" className="nx-icon-btn nx-icon-btn--danger"
                      aria-label="Delete email" title="Delete email"
                      onClick={() => handleDelete(e.id)}>
                      <NexusIcon name="Trash2" size={12} />
                    </button>
                  </span>
                </div>
                {e.subject && <div className="nx-email-card-subject">{e.subject}</div>}
                {isExpanded ? (
                  <>
                    {e.body && <pre className="nx-email-card-full-body">{e.body}</pre>}
                    {(e.toEmails?.length > 0 || e.ccEmails?.length > 0) && (
                      <div className="nx-email-card-meta-row">
                        {e.toEmails?.length > 0 && (
                          <span className="nx-email-card-meta-line">
                            <span className="nx-email-field-label">To</span>{e.toEmails.join(', ')}
                          </span>
                        )}
                        {e.ccEmails?.length > 0 && (
                          <span className="nx-email-card-meta-line">
                            <span className="nx-email-field-label">CC</span>{e.ccEmails.join(', ')}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="nx-email-card-reply-row">
                      {e.type === 'received' && (
                        <Btn variant="ghost" size="sm" icon="CornerUpLeft"
                          onClick={ev => { ev.stopPropagation(); openReply(e); }}>
                          Reply
                        </Btn>
                      )}
                      <Btn variant="ghost" size="sm" icon="CornerUpRight"
                        onClick={ev => { ev.stopPropagation(); openForward(e); }}>
                        Forward
                      </Btn>
                    </div>
                  </>
                ) : (
                  e.body && (
                    <p className="nx-email-card-preview">
                      {e.body.length > 140 ? e.body.slice(0, 140) + '…' : e.body}
                    </p>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* TagEditor — inline add/remove of tags */
const TagEditor = ({ record, type }) => {
  const store = useStore();
  const [adding, setAdding] = useStateC(false);
  const [v, setV] = useStateC('');
  const tags = record.tags || [];
  const save = () => {
    const next = v.trim();
    if (next && !tags.includes(next)) store.update(type, record.id, { tags: [...tags, next] });
    setV(''); setAdding(false);
  };
  const remove = (t) => store.update(type, record.id, { tags: tags.filter(x => x !== t) });
  return (
    <div className="nx-prop is-editable">
      <span className="nx-prop-label">Tags</span>
      <span className="nx-tag-cell nx-tag-cell-editable">
        {tags.map(t => (
          <button type="button" key={t} className="nx-tag is-removable" onClick={() => remove(t)} aria-label={'Remove tag ' + t} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            {t}<NexusIcon name="X" size={9} />
          </button>
        ))}
        {adding ? (
          <input autoFocus className="nx-tag-input" value={v} onChange={e => setV(e.target.value)} onBlur={save} onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setV(''); setAdding(false); } }} placeholder="tag…" />
        ) : (
          <button type="button" className="nx-tag is-add" onClick={() => setAdding(true)}><NexusIcon name="Plus" size={9} /> Tag</button>
        )}
      </span>
    </div>
  );
};
window.TagEditor = TagEditor;

/* Company Opportunities Tab — per-entity fetch, fully connected to backend */
const CompanyOpportunitiesTab = ({ record }) => {
  const store   = useStore();
  const toast   = useToast();
  const wsId    = store.workspaceId;

  const [localDeals, setLocalDeals] = useStateC(null);
  const [loadErr,    setLoadErr]    = useStateC(false);
  const [filter,     setFilter]     = useStateC('all');
  const [archiving,  setArchiving]  = useStateC(null);

  const storeDealCount = store.deals.filter(d => d.companyId === record.id).length;

  const fetchDeals = () => {
    if (!wsId || !record?.id) { return; }
    setLoadErr(false);
    window.__nx_api.deal.list(wsId, { companyId: record.id, pageSize: 100 })
      .then(res => {
        const raw = Array.isArray(res) ? res : (res?.data ?? []);
        const normed = raw
          .filter(d => !d.archived)
          .map(d => {
            const nd = window.__nx_api.enums.normDeal(d);
            nd.status = d.status || 'ACTIVE';
            return nd;
          });
        setLocalDeals(normed.sort((a, b) => (b.value || 0) - (a.value || 0)));
      })
      .catch(() => {
        setLoadErr(true);
        setLocalDeals([]);
      });
  };

  useEffectC(() => {
    setLocalDeals(null);
    fetchDeals();
  }, [record.id]);

  useEffectC(() => {
    if (localDeals !== null) { fetchDeals(); }
  }, [storeDealCount]);

  const today     = new Date().toISOString().split('T')[0];
  const isOverdue = (d) => d.status === 'ACTIVE' && !!d.closeDate && d.closeDate < today;

  const SYMS = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
  const fmt = (n, currency) => {
    const sym = SYMS[currency] || (currency ? currency + ' ' : '₹');
    if (n >= 10000000) { return sym + (n / 10000000).toFixed(1) + 'Cr'; }
    if (n >= 100000)   { return sym + (n / 100000).toFixed(1) + 'L'; }
    if (n >= 1000)     { return sym + (n / 1000).toFixed(1) + 'K'; }
    return sym + n;
  };

  const STATUS_TONE = { WON: 'green', LOST: 'red' };
  const allDeals     = localDeals || [];
  const wonDeals     = allDeals.filter(d => d.status === 'WON');
  const activeDeals  = allDeals.filter(d => d.status === 'ACTIVE');
  const lostDeals    = allDeals.filter(d => d.status === 'LOST');
  const overdueDeals = activeDeals.filter(isOverdue);
  const displayDeals = filter === 'active' ? activeDeals
                     : filter === 'won'    ? wonDeals
                     : filter === 'lost'   ? lostDeals
                     : allDeals;

  const totalVal        = allDeals.reduce((s, d) => s + (d.value || 0), 0);
  const wonVal          = wonDeals.reduce((s, d)  => s + (d.value || 0), 0);
  const primaryCurrency = allDeals[0]?.currency || 'INR';

  const FILTER_TABS = [
    { key: 'all',    label: 'All (' + allDeals.length + ')' },
    { key: 'active', label: 'Active (' + activeDeals.length + ')' },
    { key: 'won',    label: 'Won (' + wonDeals.length + ')' },
    { key: 'lost',   label: 'Lost (' + lostDeals.length + ')' },
  ];

  const doArchive = (dealId) => {
    if (!wsId) { return; }
    setArchiving(dealId);
    window.__nx_api.deal.archive(wsId, dealId)
      .then(() => {
        setLocalDeals(prev => (prev || []).filter(d => d.id !== dealId));
        toast({ tone: 'success', icon: 'Archive', title: 'Deal archived' });
      })
      .catch(() => toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to archive deal' }))
      .finally(() => setArchiving(null));
  };

  /* ── Loading ── */
  if (localDeals === null && !loadErr) {
    return (
      <div className="nx-drawer-page">
        <div className="nx-opp-header">
          <span className="nx-opp-count">Loading…</span>
          <Btn variant="primary" size="sm" icon="Plus"
            onClick={() => store.openCreateForm('deals', { companyId: record.id })}>New Deal</Btn>
        </div>
        <div className="nx-opp-skeleton">
          {[0, 1, 2].map(i => (
            <div key={i} className="nx-opp-skeleton-card">
              <div className="nx-skeleton-line" style={{ width: '65%' }} />
              <div className="nx-skeleton-line" style={{ width: '40%' }} />
              <div className="nx-skeleton-line" style={{ width: '85%', height: 3 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="nx-drawer-page">

      {/* ── Header: count | filter chips | New Deal ── */}
      <div className="nx-opp-header">
        <span className="nx-opp-count">
          {loadErr
            ? 'Could not load'
            : allDeals.length + ' deal' + (allDeals.length !== 1 ? 's' : '')}
        </span>
        {!loadErr && allDeals.length > 0 && (
          <div className="nx-opp-filters">
            {FILTER_TABS.map(tab => (
              <button key={tab.key} type="button"
                className={'nx-act-filter-btn' + (filter === tab.key ? ' is-active' : '')}
                onClick={() => setFilter(tab.key)}
              >{tab.label}</button>
            ))}
          </div>
        )}
        <Btn variant="primary" size="sm" icon="Plus"
          onClick={() => store.openCreateForm('deals', { companyId: record.id })}>New Deal</Btn>
      </div>

      {/* ── KPI band ── */}
      {!loadErr && allDeals.length > 0 && (
        <div className="nx-opp-kpis">
          <div className="nx-opp-kpi">
            <span className="nx-opp-kpi-val">{fmt(totalVal, primaryCurrency)}</span>
            <span className="nx-opp-kpi-label">Pipeline</span>
          </div>
          <div className={'nx-opp-kpi' + (wonDeals.length > 0 ? ' is-won' : '')}>
            <span className="nx-opp-kpi-val">{fmt(wonVal, primaryCurrency)}</span>
            <span className="nx-opp-kpi-label">Won</span>
          </div>
          <div className={'nx-opp-kpi' + (overdueDeals.length > 0 ? ' is-danger' : '')}>
            <span className="nx-opp-kpi-val">{overdueDeals.length}</span>
            <span className="nx-opp-kpi-label">Overdue</span>
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {loadErr && (
        <div className="nx-empty">
          <NexusIcon name="AlertCircle" size={22} />
          <span>Failed to load deals.</span>
          <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={fetchDeals}>Retry</Btn>
        </div>
      )}

      {/* ── Empty ── */}
      {!loadErr && allDeals.length === 0 && (
        <div className="nx-empty">
          <NexusIcon name="TrendingUp" size={28} />
          <span>No deals linked to {record.name} yet.</span>
          <Btn variant="secondary" size="sm" icon="Plus"
            onClick={() => store.openCreateForm('deals', { companyId: record.id })}>Create First Deal</Btn>
        </div>
      )}

      {/* ── No results for current filter ── */}
      {!loadErr && allDeals.length > 0 && displayDeals.length === 0 && (
        <div className="nx-empty-sm">No {filter} deals.</div>
      )}

      {/* ── Deal cards ── */}
      {!loadErr && displayDeals.length > 0 && (
        <div className="nx-popp-list">
          {displayDeals.map(d => {
            const overdue    = isOverdue(d);
            const tone       = STATUS_TONE[d.status] || 'blue';
            const probClass  = d.probability >= 70 ? ' is-high' : d.probability < 30 ? ' is-low' : '';
            const storeDeal  = store.deals.find(sd => sd.id === d.id);
            return (
              <div key={d.id} className="nx-popp-card nx-opp-card">

                {/* Clickable body — opens deal drawer */}
                <div className="nx-opp-card-body"
                  onClick={() => { window.__nx_drawer_back = record; store.openDrawer(storeDeal || d); }}>
                  <div className="nx-popp-card-head">
                    <span className="nx-popp-card-title">{d.title}</span>
                    <span className="nx-popp-card-val">{fmt(d.value, d.currency)}</span>
                  </div>
                  <div className="nx-opp-meta">
                    <Pill tone={tone}>
                      {d.status === 'WON' ? 'Won' : d.status === 'LOST' ? 'Lost' : (d.stageName || 'Active')}
                    </Pill>
                    {d.owner && (
                      <span className="nx-task-meta-chip">
                        <NexusIcon name="User" size={10} />{d.owner}
                      </span>
                    )}
                    {d.closeDate && (
                      <span className={'nx-opp-date' + (overdue ? ' is-overdue' : '')}>
                        <NexusIcon name={overdue ? 'AlertCircle' : 'Calendar'} size={10} />
                        {overdue ? 'Overdue · ' : ''}{d.closeDate}
                      </span>
                    )}
                  </div>
                  {d.probability > 0 && (
                    <div className="nx-popp-bar-wrap nx-opp-prob">
                      <div className="nx-popp-bar">
                        <div className={'nx-opp-prob-bar-fill' + probClass}
                          style={{ width: d.probability + '%' }} />
                      </div>
                      <span className="nx-popp-bar-pct">{d.probability}%</span>
                    </div>
                  )}
                </div>

                {/* Action row */}
                <div className="nx-opp-actions">
                  <button type="button" className="nx-iconbtn"
                    aria-label="Edit deal" title="Edit deal"
                    onClick={() => store.openEditForm('deals', storeDeal || d)}>
                    <NexusIcon name="Pencil" size={12} />
                  </button>
                  <button type="button" className="nx-iconbtn"
                    aria-label="Archive deal" title="Archive deal"
                    disabled={archiving === d.id}
                    onClick={() => doArchive(d.id)}>
                    <NexusIcon name={archiving === d.id ? 'Loader' : 'Archive'} size={12} />
                  </button>
                </div>

              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

window.RecordDrawer = RecordDrawer;

/* =========================================================
   COPILOT side-panel — wired to store for context
   ========================================================= */
const Copilot = ({ open, onClose }) => {
  const store = useStore();
  const toast = useToast();
  const [messages, setMessages] = useStateC([
    { from: 'ai', text: 'Hi ' + (window.__nx_auth?.user?.name?.split(' ')[0] || 'there') + ' — I can search across People, Companies, Deals, Tasks and Notes, surface overdue or inactive records, and run actions like creating follow-up tasks. What can I help with?' },
  ]);
  const [draft, setDraft] = useStateC('');
  const threadRef = useRefC(null);

  useEffectC(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffectC(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages]);

  if (!open) return null;

  const send = (text) => {
    if (!text.trim()) return;
    setMessages(m => [...m, { from: 'me', text }]);
    setDraft('');
    setTimeout(() => {
      const resp = resolveCopilotIntent(text, store, { toast });
      setMessages(m => [...m, { from: 'ai', ...resp }]);
    }, 380);
  };

  const prompts = [
    'Show overdue deals',
    'Find inactive leads',
    'Create follow-up task for Stripe',
    'Branch performance',
    'Search Razorpay',
    'Top deals by value',
  ];

  return (
    <>
      <div className="nx-drawer-veil" onClick={onClose} />
      <aside className="nx-copilot">
        <div className="nx-copilot-head">
          <span className="nx-copilot-icon"><NexusIcon name="Sparkles" size={14} /></span>
          <div className="nx-copilot-head-meta">
            <span className="nx-copilot-title">{(store.branding?.brandName || 'AI') + ' Copilot'}</span>
            <span className="nx-copilot-sub">{(window.__nx_auth?.user?.activeOrgName || window.__nx_auth?.user?.activeWorkspaceName || 'Workspace') + ' · sees your data only'}</span>
          </div>
          <IconBtn icon="X" onClick={onClose} />
        </div>

        <div className="nx-copilot-thread" ref={threadRef}>
          {messages.map((m, i) => (
            <div key={i} className={'nx-copilot-msg nx-copilot-msg-' + m.from}>
              {m.from === 'ai' && <span className="nx-copilot-avatar"><NexusIcon name="Sparkles" size={11} /></span>}
              <div className="nx-copilot-bubble">
                <p>{m.text}</p>
                <CopilotRich msg={m} store={store} />
              </div>
            </div>
          ))}
        </div>

        {messages.length <= 1 && (
          <div className="nx-copilot-suggest">
            <span className="nx-muted nx-mini">Try asking</span>
            {prompts.map(p => (
              <button key={p} className="nx-copilot-suggest-chip" onClick={() => send(p)}>{p}</button>
            ))}
          </div>
        )}

        <div className="nx-copilot-composer">
          <NexusIcon name="Sparkles" size={13} />
          <input value={draft} onChange={e => setDraft(e.target.value)} placeholder="Ask Copilot…" onKeyDown={e => e.key === 'Enter' && send(draft)} />
          <Btn variant="primary" size="sm" icon="Send" onClick={() => send(draft)}>Send</Btn>
        </div>
      </aside>
    </>
  );
};
window.Copilot = Copilot;

/* ---------- Copilot intent resolver + rich renderer ---------- */
const _wordsToDays = (s) => {
  const m = String(s || '').match(/(\d+)\s*(m|min|h|hr|d|day|w|week|mo|month|y|yr)/i);
  if (!m) return 0;
  const n = +m[1];
  const u = m[2].toLowerCase();
  if (u.startsWith('m') && !u.startsWith('mo')) return n / (60 * 24);
  if (u.startsWith('h')) return n / 24;
  if (u.startsWith('d')) return n;
  if (u.startsWith('w')) return n * 7;
  if (u.startsWith('mo')) return n * 30;
  if (u.startsWith('y')) return n * 365;
  return 0;
};

const resolveCopilotIntent = (raw, store, ctx) => {
  const text = String(raw || '').trim();
  const t = text.toLowerCase();
  const stages = store.pipeline || [];

  const norm = (s) => String(s || '').toLowerCase();
  const matchPerson = (q) => store.people.find(p => norm(p.name).includes(q) || norm(p.email).includes(q));
  const matchCompany = (q) => store.companies.find(c => norm(c.name).includes(q) || norm(c.domain).includes(q));

  /* -------- Action: create follow-up task -------- */
  if (/\b(create|add|new|schedule|set)\b.*\b(task|follow[- ]?up|reminder)\b/.test(t) || /^follow[- ]?up\s+/i.test(text)) {
    const m = text.match(/(?:for|with|to|about|on)\s+([A-Z][\w&]+(?:\s+[A-Z][\w&]+)?)/);
    let related = m ? m[1] : '';
    if (!related) {
      const p = store.people.find(p => t.includes(norm(p.name).split(' ')[0]));
      if (p) related = p.name + ' · ' + p.company;
      else {
        const c = store.companies.find(c => t.includes(norm(c.name)));
        if (c) related = c.name;
      }
    }
    const prefill = {
      title: 'Follow-up' + (related ? ' with ' + related.split('·')[0].trim() : ''),
      priority: 'Medium',
    };
    store.openCreateForm('tasks', prefill);
    ctx.toast({ tone: 'success', icon: 'CheckSquare', title: 'Task draft opened', desc: prefill.title });
    return { text: 'Opened a new follow-up task' + (related ? ' for ' + related : '') + '. Adjust and save.', kind: 'action' };
  }

  /* -------- Overdue / stale deals -------- */
  if (/\b(overdue|stale|stuck|no\s*touch|aged|untouched|sleeping|inactive\s+deal)\b/.test(t)) {
    const items = store.deals
      .filter(d => d.age >= 14 || (d.alerts || []).includes('stale') || (d.alerts || []).includes('followup'))
      .sort((a, b) => b.age - a.age)
      .slice(0, 10);
    return { text: items.length ? 'Found ' + items.length + ' overdue / stale deal' + (items.length === 1 ? '' : 's') + ' — sorted by age.' : 'No overdue deals — your pipeline is clean.', kind: 'deals', items };
  }

  /* -------- Inactive leads / cold contacts -------- */
  if (/\b(inactive|cold|dead|low.?engage|low.?score|sleeping)\b.*\b(lead|contact|people|person|customer)?\b/.test(t)) {
    const items = store.people
      .filter(p => p.score < 50 || _wordsToDays(p.lastActivity) >= 7)
      .sort((a, b) => a.score - b.score)
      .slice(0, 10);
    return { text: items.length ? 'Inactive leads (low score or >7d since last activity):' : 'No inactive leads found.', kind: 'people', items };
  }

  /* -------- Branch / location / region performance -------- */
  if (/\b(branch|location|region|city|geo|geography)\b.*\b(perform|stats|summary|breakdown|by|view|report)\b/.test(t)
   || /\bperform.*by\s+(branch|location|region|city)/.test(t)
   || /\bbranch performance|branch summary|branch report/.test(t)) {
    const buckets = {};
    store.people.forEach(p => { const k = p.city || 'Unknown'; (buckets[k] = buckets[k] || { contacts: 0, deals: 0, value: 0 }).contacts++; });
    store.deals.forEach(d => {
      const co = store.companies.find(c => c.id === d.companyId);
      const k = co?.location || 'Unknown';
      const b = (buckets[k] = buckets[k] || { contacts: 0, deals: 0, value: 0 });
      b.deals++; b.value += d.value;
    });
    const rows = Object.entries(buckets)
      .map(([k, v]) => ({ id: k, label: k, sub: v.contacts + ' contacts · ' + v.deals + ' deals', value: '$' + (v.value / 1000).toFixed(0) + 'K' }))
      .sort((a, b) => parseFloat(b.value.replace(/[$K]/g, '')) - parseFloat(a.value.replace(/[$K]/g, '')))
      .slice(0, 8);
    return { text: 'Branch performance (sorted by pipeline value):', kind: 'metrics', metrics: rows };
  }

  /* -------- Top deals / biggest deals -------- */
  if (/\b(top|biggest|largest|highest|best)\b.*\b(deal|account|customer)/.test(t)) {
    const items = store.deals.slice().sort((a, b) => b.value - a.value).slice(0, 6);
    return { text: 'Top deals by value:', kind: 'deals', items };
  }

  /* -------- Pipeline / closing / forecast -------- */
  if (/\b(pipeline|forecast|closing|close\s+this|this\s+(week|month|quarter))\b/.test(t)) {
    const openStages = (store.pipeline || []).filter(s => s.kind !== 'WON' && s.kind !== 'LOST');
    const lateStageIds = new Set(openStages.slice(Math.floor(openStages.length / 2)).map(s => s.id));
    const items = store.deals.filter(d => lateStageIds.has(d.stageId)).sort((a, b) => b.value - a.value).slice(0, 8);
    const total = items.reduce((s, d) => s + d.value, 0);
    return { text: 'Late-stage deals — total $' + (total / 1000).toFixed(0) + 'K across ' + items.length + ' deals.', kind: 'deals', items };
  }

  /* -------- Tasks / todo / today -------- */
  if (/\b(my\s+tasks|todo|to[- ]?do|open\s+tasks|today.*task|overdue\s+task)/.test(t)) {
    const items = store.tasks.filter(t => !t.done).slice(0, 10);
    return { text: items.length + ' open task' + (items.length === 1 ? '' : 's') + ':', kind: 'tasks', items };
  }

  /* -------- VIP / high intent -------- */
  if (/\b(vip|high.?intent|hot.?lead|priority\s+lead|champion)/.test(t)) {
    const items = store.people.filter(p => (p.tags || []).includes('VIP') || p.score >= 85).slice(0, 8);
    return { text: 'High-intent / VIP contacts:', kind: 'people', items };
  }

  /* -------- BFSI / NBFC / Bank segment -------- */
  if (/\b(bfsi|nbfc|bank|fintech)\b/.test(t)) {
    const items = store.people.filter(p => (p.tags || []).some(g => /BFSI|NBFC|Fintech|Bank/i.test(g))).slice(0, 8);
    return { text: 'BFSI / NBFC contacts:', kind: 'people', items };
  }

  /* -------- Summarize / account snapshot -------- */
  if (/\b(summar|snapshot|overview|status)\b/.test(t)) {
    const co = store.companies.find(c => t.includes(norm(c.name))) || matchCompany(t.replace(/.*\b(summar\w+|snapshot|overview|status)\b/, '').trim());
    if (co) {
      const deals = store.deals.filter(d => d.companyId === co.id);
      const people = store.people.filter(p => p.companyId === co.id);
      const pipe = deals.reduce((s, d) => s + d.value, 0);
      const metrics = [
        { id: 'd', label: 'Open deals', value: String(deals.length) },
        { id: 'pv', label: 'Pipeline value', value: '$' + (pipe / 1000).toFixed(0) + 'K' },
        { id: 'p', label: 'Contacts', value: String(people.length) },
        { id: 's', label: 'Company score', value: String(co.score) + (co.score != null ? ' · ' + (getScoreBand(co.score)?.label ?? '') : '') },
        { id: 'lt', label: 'Last activity', value: co.lastActivity },
      ];
      return { text: co.name + ' · ' + co.industry + ' · ' + co.location, kind: 'metrics', metrics, openId: { record: co } };
    }
  }

  /* -------- Search / find / lookup -------- */
  const searchMatch = text.match(/^(?:find|search|look ?up|where is|who is|show me|customer|lookup)\s+(.+)$/i);
  const directSearch = searchMatch ? searchMatch[1] : null;
  const q = (directSearch || text).trim().toLowerCase();
  if (q.length >= 2) {
    const ppl = store.people.filter(p => norm(p.name).includes(q) || norm(p.email).includes(q) || norm(p.company).includes(q) || norm(p.phone).replace(/\s/g, '').includes(q.replace(/\s/g, ''))).slice(0, 5);
    const cos = store.companies.filter(c => norm(c.name).includes(q) || norm(c.domain).includes(q)).slice(0, 4);
    const dls = store.deals.filter(d => norm(d.title).includes(q)).slice(0, 4);
    const tks = store.tasks.filter(tk => norm(tk.title).includes(q) || norm(tk.related).includes(q)).slice(0, 3);
    const items = [...ppl, ...cos, ...dls, ...tks];
    if (items.length) return { text: 'Found ' + items.length + ' match' + (items.length === 1 ? '' : 'es') + ' for "' + (directSearch || q) + '" across People, Companies, Deals, Tasks.', kind: 'mixed', items };
    if (directSearch) return { text: 'No matches for "' + directSearch + '".', kind: 'help' };
  }

  /* -------- Help -------- */
  if (/\b(help|what can you do|capabilities|commands|how\b)/.test(t)) {
    return { text: 'I can: search across all CRM modules, surface overdue / stale deals, find inactive leads, show branch performance, summarize accounts, list top deals, or create follow-up tasks. Just ask in plain language.', kind: 'help' };
  }

  return { text: "I couldn't find a match. Try 'show overdue deals', 'find inactive leads', 'create follow-up task for Razorpay', 'branch performance', or 'search <name>'.", kind: 'help' };
};
window.resolveCopilotIntent = resolveCopilotIntent;

const CopilotRich = ({ msg, store }) => {
  if (!msg.items && !msg.metrics) return null;
  const stages = store.pipeline || [];
  const fmt = (n) => '$' + (n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'K' : n);
  const renderItem = (r) => {
    if (r.role !== undefined) {
      return (
        <button key={r.id} className="nx-copilot-rich-row" onClick={() => store.openDrawer(r)}>
          <span>{r.name}</span>
          <span className="nx-muted nx-mini">{r.company}</span>
          {typeof r.score === 'number' && <strong style={{ color: r.score >= 80 ? '#0E8C5A' : r.score < 50 ? '#DE3217' : '#C26A1F' }}>{r.score}</strong>}
        </button>
      );
    }
    if (r.domain !== undefined) {
      return (
        <button key={r.id} className="nx-copilot-rich-row" onClick={() => store.openDrawer(r)}>
          <span>{r.name}</span>
          <span className="nx-muted nx-mini">{r.industry}</span>
          <Pill tone="purple">{r.lifecycle}</Pill>
        </button>
      );
    }
    if (r.stageId !== undefined) {
      const stage = stages.find(s => s.id === r.stageId);
      return (
        <button key={r.id} className="nx-copilot-rich-row" onClick={() => store.openDrawer(r)}>
          <span>{r.title}</span>
          <strong>{fmt(r.value)}</strong>
          <Pill tone="amber">{stage ? stage.name : '—'}</Pill>
          {r.age >= 14 && <Pill tone="red">{r.age}d</Pill>}
        </button>
      );
    }
    if (r.status !== undefined) {
      return (
        <button key={r.id} className="nx-copilot-rich-row" onClick={() => { store.update('tasks', r.id, { done: !r.done }); }}>
          <span>{r.title}</span>
          {r.dueDate && <span className="nx-muted nx-mini">{r.dueDate}</span>}
          <Pill tone={r.priority === 'High' ? 'red' : r.priority === 'Medium' ? 'amber' : 'gray'}>{r.priority}</Pill>
        </button>
      );
    }
    return null;
  };
  return (
    <div className="nx-copilot-rich">
      {msg.items && msg.items.map(renderItem)}
      {msg.metrics && msg.metrics.map(m => (
        <div key={m.id} className="nx-copilot-rich-row">
          <span>{m.label}</span>
          {m.sub && <span className="nx-muted nx-mini">{m.sub}</span>}
          <strong>{m.value}</strong>
        </div>
      ))}
      {msg.openId && msg.openId.record && (
        <button className="nx-copilot-action" onClick={() => store.openDrawer(msg.openId.record)}>
          <NexusIcon name="ArrowRight" size={10} /> Open record
        </button>
      )}
    </div>
  );
};
window.CopilotRich = CopilotRich;

/* =========================================================
   COMMAND PALETTE — wired to store
   ========================================================= */
const CommandPalette = ({ open, onClose, onNavigate }) => {
  const store = useStore();
  const [q, setQ] = useStateC('');
  const [cursor, setCursor] = useStateC(0);
  const inputRef = useRefC(null);

  useEffectC(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (open) { setQ(''); setCursor(0); }
  }, [open]);

  // Build flat list for keyboard nav
  const ql = q.toLowerCase();

  // Recent records from sessionStorage (shown when no query typed)
  const recentRecords = useRefC(null);
  if (!ql) {
    try {
      const ids = JSON.parse(sessionStorage.getItem('nx-recent-records') || '[]');
      recentRecords.current = ids.map(({ id, type }) => {
        const record = type === 'person'  ? store.people.find(p => p.id === id)
                     : type === 'company' ? store.companies.find(c => c.id === id)
                     : type === 'deal'    ? store.deals.find(d => d.id === id)
                     : null;
        return record ? { record, type } : null;
      }).filter(Boolean).slice(0, 5);
    } catch (_) { recentRecords.current = []; }
  } else {
    recentRecords.current = [];
  }
  const recentList = recentRecords.current || [];

  const peopleMatches  = store.people.filter(p => (p.name || '').toLowerCase().includes(ql) || (p.email || '').toLowerCase().includes(ql)).slice(0, 4);
  const companyMatches = store.companies.filter(c => (c.name || '').toLowerCase().includes(ql)).slice(0, 3);
  const dealMatches    = store.deals.filter(d => (d.title || '').toLowerCase().includes(ql)).slice(0, 3);
  const taskMatches    = store.tasks.filter(t => (t.title || '').toLowerCase().includes(ql)).slice(0, 3);
  const noteMatches    = store.notes.filter(n => ((n.detail || '').toLowerCase().includes(ql) || (n.noteTitle || '').toLowerCase().includes(ql))).slice(0, 3);

  const actions = [
    { id: 'new-person',  icon: 'UserPlus', label: 'New Person',          kbd: 'P', run: () => store.openCreateForm('people') },
    { id: 'new-company', icon: 'Building', label: 'New Company',         kbd: 'C', run: () => store.openCreateForm('companies') },
    { id: 'new-deal',    icon: 'Target',   label: 'New Deal',            kbd: 'D', run: () => store.openCreateForm('deals') },
    { id: 'new-task',    icon: 'CheckSquare', label: 'New Task',         kbd: 'T', run: () => store.openCreateForm('tasks') },
    { id: 'log-call',    icon: 'Phone',         label: 'Log Call',           kbd: '', run: () => store.logActivity({ type: 'call',     who: window.__nx_auth?.user?.name || 'You', what: 'logged a call' }) },
    { id: 'log-meeting', icon: 'Calendar',      label: 'Log Meeting',        kbd: '', run: () => store.logActivity({ type: 'meeting',  who: window.__nx_auth?.user?.name || 'You', what: 'logged a meeting' }) },
    { id: 'log-followup',icon: 'Clock',         label: 'Schedule Follow-up', kbd: '', run: () => store.logActivity({ type: 'followup', who: window.__nx_auth?.user?.name || 'You', what: 'scheduled a follow-up' }) },
    { id: 'log-whatsapp',icon: 'MessageCircle', label: 'Log WhatsApp',       kbd: '', run: () => store.logActivity({ type: 'whatsapp', who: window.__nx_auth?.user?.name || 'You', what: 'logged a WhatsApp message' }) },
    { id: 'goto-pipeline', icon: 'Target',      label: 'Go To Pipeline',     kbd: '⌘1', run: () => onNavigate('deals') },
    { id: 'goto-people',   icon: 'User',        label: 'Go To People',       kbd: '⌘2', run: () => onNavigate('people') },
    { id: 'goto-tasks',    icon: 'CheckSquare', label: 'Go To Tasks',        kbd: '⌘3', run: () => onNavigate('tasks') },
    { id: 'goto-notes',    icon: 'Note',        label: 'Go To Notes',        kbd: '⌘4', run: () => onNavigate('notes') },
  ].filter(a => !ql || a.label.toLowerCase().includes(ql));

  const flat = [
    ...recentList.map(r => ({ kind: 'recent', record: r.record, type: r.type })),
    ...peopleMatches.map(p => ({ kind: 'person', record: p })),
    ...companyMatches.map(c => ({ kind: 'company', record: c })),
    ...dealMatches.map(d => ({ kind: 'deal', record: d })),
    ...taskMatches.map(t => ({ kind: 'task', record: t })),
    ...noteMatches.map(n => ({ kind: 'note', record: n })),
    ...actions.map(a => ({ kind: 'action', action: a })),
  ];

  useEffectC(() => { setCursor(0); }, [q]);

  useEffectC(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, flat.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
      if (e.key === 'Enter') {
        e.preventDefault();
        const sel = flat[cursor];
        if (!sel) return;
        if (sel.kind === 'action') sel.action.run();
        else store.openDrawer(sel.record);
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, cursor, flat, onClose, store]);

  if (!open) return null;

  let idx = -1;
  const renderRow = (key, body, onClick) => {
    idx++;
    const isActive = idx === cursor;
    return <button key={key} className={'nx-cmd-row' + (isActive ? ' is-active' : '')} onClick={onClick} onMouseEnter={(() => { const myIdx = idx; return () => setCursor(myIdx); })()}>{body}</button>;
  };

  return (
    <div className="nx-cmd-veil" onClick={onClose}>
      <div className="nx-cmd" onClick={e => e.stopPropagation()}>
        <div className="nx-cmd-input">
          <NexusIcon name="Search" size={14} />
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="Search People, Companies, Deals, Tasks, Notes Or Run A Command…" />
          <Kbd>esc</Kbd>
        </div>
        <div className="nx-cmd-results">
          {recentList.length > 0 && (
            <div className="nx-cmd-section">
              <span className="nx-cmd-section-label">Recent</span>
              {recentList.map(({ record: r, type: t }) => {
                const icon = t === 'person' ? null : t === 'deal' ? 'Target' : 'Building2';
                const sub  = t === 'person' ? (r.role || '') + (r.company ? ' · ' + r.company : '') : t === 'deal' ? '$' + (r.value||0).toLocaleString() : r.industry || '';
                return renderRow(r.id + '-recent', <>
                  {t === 'person' ? <Avatar name={r.name} tone={r.tone} size={20} /> : <span className="nx-cmd-icon"><NexusIcon name={icon} size={12} /></span>}
                  <span className="nx-cmd-row-name">{r.name || r.title}</span>
                  <span className="nx-muted">{sub}</span>
                  <NexusIcon name="Clock" size={10} style={{ opacity: 0.4 }} />
                </>, () => { store.openDrawer(r); onClose(); });
              })}
            </div>
          )}
          {peopleMatches.length > 0 && (
            <div className="nx-cmd-section">
              <span className="nx-cmd-section-label">People</span>
              {peopleMatches.map(p => renderRow(p.id, <>
                <Avatar name={p.name} tone={p.tone} size={20} />
                <span className="nx-cmd-row-name">{p.name}</span>
                <span className="nx-muted">{p.role} · {p.company}</span>
                <NexusIcon name="ArrowRight" size={11} />
              </>, () => { store.openDrawer(p); onClose(); }))}
            </div>
          )}
          {companyMatches.length > 0 && (
            <div className="nx-cmd-section">
              <span className="nx-cmd-section-label">Companies</span>
              {companyMatches.map(c => renderRow(c.id, <>
                <span className="nx-domain-mark">{c.name[0]}</span>
                <span className="nx-cmd-row-name">{c.name}</span>
                <span className="nx-muted">{c.industry} · {c.location}</span>
              </>, () => { store.openDrawer(c); onClose(); }))}
            </div>
          )}
          {dealMatches.length > 0 && (
            <div className="nx-cmd-section">
              <span className="nx-cmd-section-label">Deals</span>
              {dealMatches.map(d => renderRow(d.id, <>
                <span className="nx-cmd-icon"><NexusIcon name="Target" size={12} /></span>
                <span className="nx-cmd-row-name">{d.title}</span>
                <span className="nx-muted">${d.value.toLocaleString()}</span>
              </>, () => { store.openDrawer(d); onClose(); }))}
            </div>
          )}
          {taskMatches.length > 0 && (
            <div className="nx-cmd-section">
              <span className="nx-cmd-section-label">Tasks</span>
              {taskMatches.map(t => renderRow(t.id, <>
                <span className="nx-cmd-icon"><NexusIcon name="CheckSquare" size={12} /></span>
                <span className="nx-cmd-row-name">{t.title}</span>
                <span className="nx-muted">{t.dueDate || ''}</span>
              </>, () => { store.openDrawer(t); onClose(); }))}
            </div>
          )}
          {noteMatches.length > 0 && (
            <div className="nx-cmd-section">
              <span className="nx-cmd-section-label">Notes</span>
              {noteMatches.map(n => renderRow(n.id, <>
                <span className="nx-cmd-icon"><NexusIcon name="StickyNote" size={12} /></span>
                <span className="nx-cmd-row-name">{n.noteTitle || n.detail}</span>
                <span className="nx-muted">{n.time}</span>
              </>, () => { onNavigate('notes'); onClose(); }))}
            </div>
          )}
          {actions.length > 0 && (
            <div className="nx-cmd-section">
              <span className="nx-cmd-section-label">Actions</span>
              {actions.map(a => renderRow(a.id, <>
                <span className="nx-cmd-icon"><NexusIcon name={a.icon} size={12} /></span>
                <span className="nx-cmd-row-name">{a.label}</span>
                <span style={{ flex: 1 }} />
                {a.kbd && <Kbd>{a.kbd}</Kbd>}
              </>, () => { a.run(); onClose(); }))}
            </div>
          )}
          {flat.length === 0 && <div className="nx-cmd-empty">No matches for "{q}"</div>}
        </div>
        <div className="nx-cmd-foot">
          <span><Kbd>↑</Kbd><Kbd>↓</Kbd>navigate</span>
          <span><Kbd>↵</Kbd>open</span>
          <span><Kbd>⌘K</Kbd>toggle</span>
        </div>
      </div>
    </div>
  );
};
window.CommandPalette = CommandPalette;
